import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Paper,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Avatar,
  Fab,
  CardHeader,
  LinearProgress,
  alpha,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Science as ScienceIcon,
  People as PeopleIcon,
  Event as EventIcon,
  PendingActions as PendingIcon,
  EditCalendar as EditCalendarIcon,
  Diversity3 as TeamIcon,
  Backup as UploadIcon,
  LocationOn as LocationIcon,
  NoteAdd as NotesIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Person2Outlined,
  Business as BusinessIcon,
  Map as MapIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Preview as PreviewIcon,
  CameraAlt as CameraAltIcon,
  AccessTime as ClockIcon,
  Image as ImageIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Reply as ReplyIcon,
  Timeline as TimelineIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import {
  encryptText,
  encryptAppId,
  decryptText,
  decryptAppId,
} from "../../../utils/encryption";
import FileUploadComponent from "../../../components/FileUploadComponent";
import s3Utils from "../../../utils/s3Utils";
import PageTitle from "../../../components/PageTitle";
import useAssessmentTracking from "../../../hooks/useAssessmentTracking";
import {
  LocationWarningDialog,
  HourlyAttendanceDialog,
} from "../../../components/AssessmentTrackingDialogs";
import DoApiService from "../../../services/DoApiService";

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
    contrast: "#ffffff",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const AssessmentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to parse DD-MM-YYYY HH:MM:SS format to Date object
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    try {
      // Format: "19-12-2025 11:42:00"
      const [datePart, timePart] = dateString.split(" ");
      const [day, month, year] = datePart.split("-");
      const [hours, minutes, seconds] = timePart.split(":");
      // JavaScript Date constructor: new Date(year, monthIndex, day, hours, minutes, seconds)
      // Note: monthIndex is 0-based (0 = January, 11 = December)
      return new Date(year, month - 1, day, hours, minutes, seconds);
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptAppId(encryptedAssessorId);
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [expanded, setExpanded] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [naf1DialogOpen, setNaf1DialogOpen] = useState(false);
  const [naf1File, setNaf1File] = useState(null);
  const [isNaf1Uploaded, setIsNaf1Uploaded] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [openOfficerDialog, setOpenOfficerDialog] = useState(false);
  const [dealingOfficer, setDealingOfficer] = useState(null);
  const [loadingOfficer, setLoadingOfficer] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [refreshing, setRefreshing] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [meetingData, setMeetingData] = useState(null);
  const [loadingMeeting, setLoadingMeeting] = useState(false);
  const [meetingType, setMeetingType] = useState("");
  const [labType, setLabType] = useState("3");
  const [userType, setUserType] = useState(0);
  const [medicalImaging, setMedicalImaging] = useState("0");
  const [labData, setLabData] = useState({});
  const [clarificationType, setClarificationType] = useState("");
  const [feedback, setFeedback] = useState("False");

  // API error state
  const [apiError, setApiError] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Hourly Attendance Dialog state
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [attendanceImages, setAttendanceImages] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [assessorsData, setAssessorsData] = useState([]);
  const [assessorName, setAssessorName] = useState("");
  const [assessmentStage, setAssessmentStage] = useState("");
  const [finalCheckData, setFinalCheckData] = useState("");
  const [noNc, setNoNc] = useState(0);
  const [canCompleteApplicationReview, setCanCompleteApplicationReview] =
    useState(false);
  const [applicationReviewMissingSections, setApplicationReviewMissingSections] =
    useState([]);
  const [updatingApplicationReview, setUpdatingApplicationReview] =
    useState(false);
  const [completeReviewDialogOpen, setCompleteReviewDialogOpen] =
    useState(false);

  // Send to NABL dialog state
  const [sendToNABLDialogOpen, setSendToNABLDialogOpen] = useState(false);
  const [sendingToNABL, setSendingToNABL] = useState(false);

  // NC Action buttons state
  const [sendingToLabFromNC, setSendingToLabFromNC] = useState(false);
  const [sendingToNABLFromNC, setSendingToNABLFromNC] = useState(false);

  // NC Action confirmation dialogs state
  const [sendToLabConfirmDialogOpen, setSendToLabConfirmDialogOpen] =
    useState(false);
  const [sendToNABLConfirmDialogOpen, setSendToNABLConfirmDialogOpen] =
    useState(false);

  // Assessor action button state
  const [processingAssessorAction, setProcessingAssessorAction] =
    useState(false);

  // Trail dialog state
  const [trailDialogOpen, setTrailDialogOpen] = useState(false);
  const [selectedTrailData, setSelectedTrailData] = useState(null);

  // Clarification dialog state
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [selectedNcForClarification, setSelectedNcForClarification] =
    useState(null);
  const [clarificationRemarks, setClarificationRemarks] = useState("");
  const [sendingClarification, setSendingClarification] = useState(false);
  const [applicationStage, setApplicationStage] = useState("");
  const [finalMessage, setFinalMessage] = useState("");

  // Declaration acceptance state
  const [acceptingDeclaration, setAcceptingDeclaration] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
    fetchlabstagedetails,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });

  // Check if application review can be completed
  const checkApplicationReviewStatus = async () => {
    if (!assessorId || !labData.assessmentId || !appId) {
      return;
    }

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CheckApplication?assessorId=${assessorId}&assessmentId=${labData.assessmentId}&appId=${appId}`;
      const response = await axios.get(url);

      if (response.data && response.data.isstatus === true) {
        setCanCompleteApplicationReview(true);
        setApplicationReviewMissingSections([]);
      } else {
        setCanCompleteApplicationReview(false);
        const sections = (response.data?.message || "")
          .split("~")
          .map((s) => s.trim())
          .filter(Boolean);
        setApplicationReviewMissingSections(sections);
      }
    } catch (error) {
      console.error("Error checking application review status:", error);
      setCanCompleteApplicationReview(false);
      setApplicationReviewMissingSections([]);
    }
  };

  // Check if all NCs have assessor as the latest reply and assessor stage is 5
  const checkCanShowAssessorActionButton = () => {
    // Check if assessor stage is 5h
    if (assessmentStage !== "5h") {
      return false;
    }

    // If no NCs, return false
    if (nonConformities.length === 0) {
      return false;
    }

    // Check if all NCs have assessor as the latest reply
    return nonConformities.every((nc) => {
      // Check if ncreply array exists and has replies
      if (
        !nc.ncreply ||
        !Array.isArray(nc.ncreply) ||
        nc.ncreply.length === 0
      ) {
        return false;
      }

      // Get the last reply in the array (most recent)
      const lastReply = nc.ncreply[nc.ncreply.length - 1];

      // Check if the last reply is from Assessor
      return lastReply.replyby === "Assessor";
    });
  };

  // Check if can show Send to NABL button (when isclsosed=true AND last reply is from Assessor)
  const checkCanShowSendToNABLButton = () => {
    // Check if assessor stage is 5h
    if (assessmentStage !== "5h") {
      return false;
    }
    // If no NCs, return false
    if (nonConformities.length === 0) {
      return false;
    }

    // Check if all NCs have isclsosed=true AND assessor as the latest reply
    const allClosedWithAssessorReply = nonConformities.every((nc) => {
      // Check if NC is closed
      const isClosed =
        nc.isclsosed === true ||
        nc.isclsosed === "True" ||
        nc.isclsosed === "1";

      // Check if ncreply array exists and has replies
      if (
        !nc.ncreply ||
        !Array.isArray(nc.ncreply) ||
        nc.ncreply.length === 0
      ) {
        return false;
      }

      // Get the last reply in the array (most recent)
      const lastReply = nc.ncreply[nc.ncreply.length - 1];

      // Check if the last reply is from Assessor
      const lastReplyIsAssessor = lastReply.replyby === "Assessor";

      return isClosed && lastReplyIsAssessor;
    });

    console.log(
      "All NCs closed with Assessor as last reply:",
      allClosedWithAssessorReply,
    );
    return allClosedWithAssessorReply;
  };

  // Check if can show Send to Lab button:
  // At least one NC must be OPEN, AND all NCs must be closed OR have assessor as last reply.
  // If all NCs are closed → show Send to NABL only, not Send to Lab.
  const checkCanShowSendToLabButton = () => {
    if (assessmentStage !== "5h") {
      return false;
    }
    if (nonConformities.length === 0) {
      return false;
    }

    const isNcClosed = (nc) =>
      nc.isclsosed === true ||
      nc.isclsosed === "True" ||
      nc.isclsosed === "1" ||
      nc.isclsosed === 1;

    // Must have at least one open NC — otherwise use Send to NABL
    const hasOpenNC = nonConformities.some((nc) => !isNcClosed(nc));
    if (!hasOpenNC) return false;

    // All NCs must be closed OR have assessor as last reply
    const allNcsSatisfied = nonConformities.every((nc) => {
      if (isNcClosed(nc)) return true;

      if (!nc.ncreply || !Array.isArray(nc.ncreply) || nc.ncreply.length === 0) {
        return false;
      }

      const lastReply = nc.ncreply[nc.ncreply.length - 1];
      return lastReply.replyby === "Assessor";
    });

    console.log("Send to Lab eligible (has open NC + all satisfied):", allNcsSatisfied);
    return allNcsSatisfied;
  };

  // Open confirmation dialog for completing application review
  const handleOpenCompleteReviewDialog = () => {
    setCompleteReviewDialogOpen(true);
  };

  // Close confirmation dialog
  const handleCloseCompleteReviewDialog = () => {
    setCompleteReviewDialogOpen(false);
  };

  // Update application review as completed
  const handleCompleteApplicationReview = async () => {
    if (!assessorId || !labData.assessmentId || !appId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setUpdatingApplicationReview(true);
    setCompleteReviewDialogOpen(false); // Close dialog

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateApplication?assessorid=${assessorId}&assessmentid=${labData.assessmentId}&appid=${appId}`;
      const response = await axios.put(url);

      if (
        response.data &&
        (response.data.isstatus === true || response.data.success)
      ) {
        showSnackbar(
          response.data.message || "Application review completed successfully",
          "success",
        );
        setCanCompleteApplicationReview(false);
        // Refresh lab details to update the completion status
        fetchLabDetails();
      } else {
        showSnackbar(
          response.data.message || "Failed to complete application review",
          "error",
        );
      }
    } catch (error) {
      console.error("Error completing application review:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while completing application review",
        "error",
      );
    } finally {
      setUpdatingApplicationReview(false);
    }
  };

  // Fetch assessment status from CheckAssessment API
  const fetchAssessmentStatus = async (assessmentId, userType) => {
    if (!assessorId || !assessmentId || !appId) {
      return {};
    }

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CheckAssessment?assessorId=${assessorId}&assessmentId=${assessmentId}&appId=${appId}&type=${userType}`;

      const response = await axios.get(url);

      if (
        response.data &&
        response.data.isstatus &&
        Array.isArray(response.data.check)
      ) {
        const statusMap = {};
        response.data.check.forEach((item) => {
          statusMap[item.section] = item.status === "True";
        });
        setFeedback(statusMap["FeedBack"] || "False");

        return {
          applicationReview: statusMap["Application Preview"] || false,
          checklist: statusMap["CheckList"] || false,
          testWitness: statusMap["Test Witness"] || false,
          equipment: statusMap["Equipment"] || false,
          tada: statusMap["TADA"] || false,
          feedback: statusMap["FeedBack"] || false,
          naf6: statusMap["NAF6"] || false,
        };
      }
      return {};
    } catch (error) {
      console.error("❌ Error fetching assessment status:", error);
      return {};
    }
  };

  // Fetch lab details from API
  const fetchLabDetails = async () => {
    try {
      setRefreshing(true);
      setApiError(false);
      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails`;

      const params = {
        appid: appId,
        orgid: assessorId,
      };
      const response = await axios.get(apiUrl, { params });
      if (response.data && response.data.isstatus) {
        const lab = response.data;

        // Fetch assessment status from CheckAssessment API
        const assessmentStatus = await fetchAssessmentStatus(
          lab.assessmentId,
          lab.capacity,
        );
        await loadFinalResultData(lab.assessmentId);

        setLabData({
          labType: getLabTypeName(lab.labType),
          capacity: capacityMap[lab.capacity] || "-",
          assessmentDateFrom: lab.assessmentDateFrom || "",
          assessmentDateTo: lab.assessmentDateTo || "",
          applicationStatus: lab.applicationStatus || "",
          currentActivity: lab.currentActivity || "",
          labName: lab.labName || "",
          labAddress: lab.labAddress || "",
          labId: lab.labId || "",
          standard: lab.standard || "",
          location: lab.location || "",
          assessmentId: lab.assessmentId || "",
          startAssessment: lab.startAssessment === "true" ? true : false,
          startAssessmentDate: lab.startAssessmentDate
            ? new Date(lab.startAssessmentDate)
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })
                .replace(",", "")
            : "",
          stopAssessment: lab.stopAssessment === "true" ? true : false,
          stopAssessmentDate: lab.stopAssessmentDate
            ? new Date(lab.stopAssessmentDate)
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })
                .replace(",", "")
            : "",
          openingMeeting: lab.openingMeeting === "true" ? true : false,
          openingMeetingDate: lab.openingMeetingDate
            ? new Date(lab.openingMeetingDate)
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })
                .replace(",", "")
            : "",
          closingMeeting: lab.closingMeeting === "true" ? true : false,
          closingMeetingDate: lab.closingMeetingDate
            ? new Date(lab.closingMeetingDate)
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })
                .replace(",", "")
            : "",
          applicationReview:
            assessmentStatus.applicationReview !== undefined
              ? assessmentStatus.applicationReview
              : lab.applicationReview === "true",
          checklist:
            assessmentStatus.checklist !== undefined
              ? assessmentStatus.checklist
              : lab.checklist === "true",
          testWitness:
            assessmentStatus.testWitness !== undefined
              ? assessmentStatus.testWitness
              : lab.testWitness === "true",
          tada:
            assessmentStatus.tada !== undefined
              ? assessmentStatus.tada
              : lab.tada === "true",
          equipment:
            assessmentStatus.equipment !== undefined
              ? assessmentStatus.equipment
              : lab.equipment === "true",
          applicationPreviewFile: lab.applicationPreviewFile
            ? await s3Utils.generateSignedUrlForFilename(
                lab.applicationPreviewFile,
              )
            : "",
          labLatitude: lab.labLatitude || "",
          labLongitude: lab.labLongitude || "",
          applatitude: lab.applatitude || "",
          applongitude: lab.applongitude || "",
          appAddress: lab.appAddress || "",
          startSelfie: lab.startSelfie
            ? await s3Utils.generateSignedUrlForFilename(lab.startSelfie)
            : "",
          stopSelfie: lab.stopSelfie
            ? await s3Utils.generateSignedUrlForFilename(lab.stopSelfie)
            : "",
          contactPerson: lab.contactPerson || "",
          contactEmail: lab.contactEmail || "",
          contactMobile: lab.contactMobile || "",
          labhead: lab.labhead || "",
          labheadMobile: lab.labheadMobile || "",
          labheadEmail: lab.labheadEmail || "",
          feedback:
            assessmentStatus.feedback !== undefined
              ? assessmentStatus.feedback
              : lab.feedback === "true",
          naf6:
            assessmentStatus.naf6 !== undefined
              ? assessmentStatus.naf6
              : lab.naf6 === "true",
          acceptDeclaration: lab.declaration === "1" ? true : false,
        });
        setAssessmentStage(lab.assessmentStage || "");
        setNoNc(lab.noNc === "1" ? 1 : 0);
        setLabType(lab.labType || "3");
        setUserType(lab.capacity || 0);
        setMedicalImaging(lab.medicalImaging || "0");
        setIsNaf1Uploaded(lab.naf1Uploaded === "true" ? true : false);
        if (lab.naf1File) {
          setNaf1File(lab.naf1File);
        } else {
          setNaf1File(null);
        }
      } else {
        console.warn("⚠️ API response isstatus is false");
        setApiError(true);
        showSnackbar(
          response.data?.message || "Failed to load lab details",
          "error",
        );
      }
    } catch (error) {
      console.error("❌ Error fetching lab details:", error);
      setApiError(true);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error loading lab details. Please try again.",
        "error",
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Show Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle NAF1 Upload
  const handleUploadNaf1 = async () => {
    if (!naf1File) {
      showSnackbar("Please select a file to upload", "error");
      return;
    }

    if (!appId || !labData.assessmentId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    try {
      const payload = {
        naF1file: naf1File,
        appid: String(appId),
        assessmentid: String(labData.assessmentId),
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UploadNAF1`;
      const response = await axios.put(url, payload);

      // Handle .NET API response format
      if (response.data.success || response.data.isstatus) {
        showSnackbar("NAF1 uploaded successfully", "success");
        setIsNaf1Uploaded(true);
        setNaf1DialogOpen(false);
        await fetchLabDetails(); // Refresh data
      } else {
        throw new Error(response.data.message || "Failed to upload NAF1");
      }
    } catch (error) {
      console.error("❌ Error uploading NAF1:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error uploading NAF1",
        "error",
      );
    }
  };

  // Fetch Non-Conformities from API
  const fetchNonConformities = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      return;
    }

    try {
      setLoadingNC(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMain?appid=${appId}&assessmentid=${labData.assessmentId}&assessorid=${assessorId}`;
      const response = await axios.get(url);

      if (
        response.data.isstatus &&
        response.data.nc &&
        Array.isArray(response.data.nc)
      ) {
        const formattedNC = response.data.nc.map((item) => ({
          id: item.id,
          clause: item.clauseno,
          remarks: item.remarks || "",
          description: item.description || "",
          document: item.document || "",
          ncid: item.ncid,
          cabReply: item.cabReply || "",
          cabReplyDate: item.cabReplyDate || "",
          isLabAccept: item.isLabAccept || "",
          assessorRemarks: item.assessorRemarks || "",
          stage: item.stage || "",
          status: item.status || "", // Add status field
          isclsosed: item.isclsosed || "False",
          assessorName: item.assessorName || "",
          ncreply: item.ncreply || [],
        }));

        setNonConformities(formattedNC);
      } else {
        setNonConformities([]);
      }
    } catch (error) {
      console.error("❌ Error fetching non-conformities:", error);
      showSnackbar("Error loading non-conformities", "error");
      setNonConformities([]);
    } finally {
      setLoadingNC(false);
    }
  };
  const fetchNonConformitiesAll = async () => {
    if (!appId || !labData.assessmentId) {
      return;
    }

    try {
      setLoadingNCAll(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMainall?appid=${appId}&assessmentid=${labData.assessmentId}`;
      const response = await axios.get(url);

      if (
        response.data.isstatus &&
        response.data.nc &&
        Array.isArray(response.data.nc)
      ) {
        const formattedNCAll = response.data.nc.map((item) => ({
          id: item.id,
          clause: item.clauseno,
          remarks: item.remarks || "",
          description: item.description || "",
          document: item.document || "",
          ncid: item.ncid,
          cabReply: item.cabReply || "",
          cabReplyDate: item.cabReplyDate || "",
          isLabAccept: item.isLabAccept || "",
          assessorRemarks: item.assessorRemarks || "",
          assessorName: item.assessorName || "",
          stage: item.stage || "",
          isclsosed: item.isclsosed || "False",
          status: item.status || "", // Add status field
          ncreply: item.ncreply || [],
        }));

        setNonConformitiesAll(formattedNCAll);
      } else {
        setNonConformitiesAll([]);
      }
    } catch (error) {
      console.error("❌ Error fetching non-conformities:", error);
      showSnackbar("Error loading non-conformities", "error");
      setNonConformitiesAll([]);
    } finally {
      setLoadingNCAll(false);
    }
  };

  // Handle No Non-Conformities Observed
  const handleNoNCObserved = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    try {
      const payload = {
        stage: "1",
        appid: String(appId),
        assessmentid: String(labData.assessmentId),
        assessorid: String(assessorId),
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessmnetstage`;
      const response = await axios.put(url, payload);

      if (response.data.isstatus) {
        showSnackbar(
          response.data.message || "Assessment stage updated successfully",
          "success",
        );
        await fetchLabDetails(); // Refresh data
      } else {
        throw new Error(
          response.data.message || "Failed to update assessment stage",
        );
      }
    } catch (error) {
      console.error("❌ Error updating assessment stage:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error updating assessment stage",
        "error",
      );
    }
  };

  // Handle Send to NABL (Update stage to 21)
  const handleSendToNABL = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setSendingToNABL(true);
    try {
      const payload = {
        appId: String(appId),
        assessmentId: String(labData.assessmentId),
        assessorId: String(assessorId),
      };

      const url = `${process.env.REACT_APP_API_URL}/laboratory/send-to-nabl`;
      const response = await axios.post(url, payload);

      if (response.data.success) {
        showSnackbar(
          response.data.message || "Assessment sent to NABL successfully",
          "success",
        );
        setTimeout(() => {
          navigate("/main/assessor/application-assessment");
        }, 3000);
        setSendToNABLDialogOpen(false); // Close dialog
        // await fetchLabDetails(); // Refresh data
      } else {
        throw new Error(
          response.data.message || "Failed to send assessment to NABL",
        );
      }
    } catch (error) {
      console.error("❌ Error sending assessment to NABL:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error sending assessment to NABL",
        "error",
      );
    } finally {
      setSendingToNABL(false);
    }
  };

  // Handle Assessor Action when all NCs have assessor replies and stage is 5
  const handleAssessorAction = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setProcessingAssessorAction(true);
    try {
      // You can define what action should be taken here
      // For now, let's update the assessment stage or call a specific API
      const payload = {
        appId: String(appId),
        assessmentId: String(labData.assessmentId),
        assessorId: String(assessorId),
        stage: "5g", // Move to next stage
      };

      // Replace this URL with the actual API endpoint you want to call
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessorStage?appId=${appId}&assessmentId=${labData.assessmentId}&assessorId=${assessorId}&stage=5g`;
      const response = await axios.put(url);

      if (response.data.isstatus) {
        showSnackbar(
          response.data.message || "Assessment action completed successfully",
          "success",
        );
        // Refresh data
        await fetchLabDetails();
        await fetchNonConformities();
        await fetchNonConformitiesAll();
      } else {
        throw new Error(
          response.data.message || "Failed to complete assessment action",
        );
      }
    } catch (error) {
      console.error("❌ Error completing assessment action:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error completing assessment action",
        "error",
      );
    } finally {
      setProcessingAssessorAction(false);
    }
  };

  // Handle Send to Lab from NC (when any status is 0 or "")
  const handleSendToLabFromNC = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setSendingToLabFromNC(true);
    try {
      const payload = {
        appId: String(appId),
        assessmentId: String(labData.assessmentId),
        assessorId: String(assessorId),
        stage: "5g", // Update stage to send back to lab
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessorStage?appId=${appId}&assessmentId=${labData.assessmentId}&assessorId=${assessorId}&stage=5g`;
      const response = await axios.put(url);

      if (response.data.isstatus) {
        showSnackbar(
          response.data.message || "Assessment sent to Lab successfully",
          "success",
        );
        // Refresh data
        await fetchLabDetails();
        await fetchNonConformities();
        await fetchNonConformitiesAll();
      } else {
        throw new Error(
          response.data.message || "Failed to send assessment to Lab",
        );
      }
    } catch (error) {
      console.error("❌ Error sending assessment to Lab:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error sending assessment to Lab",
        "error",
      );
    } finally {
      setSendingToLabFromNC(false);
    }
  };

  // Handle Send to NABL from NC (when all status is 1 or 2)
  const handleSendToNABLFromNC = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setSendingToNABLFromNC(true);
    try {
      const payload = {
        appId: String(appId),
        assessmentId: String(labData.assessmentId),
        assessorId: String(assessorId),
        stage: "5o", // Update stage to send to NABL
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessorStage?appId=${appId}&assessmentId=${labData.assessmentId}&assessorId=${assessorId}&stage=5o`;
      const response = await axios.put(url);

      if (response.data.isstatus) {
        showSnackbar(
          response.data.message || "Assessment sent to NABL successfully",
          "success",
        );
        // Refresh data
        await fetchLabDetails();
        await fetchNonConformities();
        await fetchNonConformitiesAll();
      } else {
        throw new Error(
          response.data.message || "Failed to send assessment to NABL",
        );
      }
    } catch (error) {
      console.error("❌ Error sending assessment to NABL:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error sending assessment to NABL",
        "error",
      );
    } finally {
      setSendingToNABLFromNC(false);
    }
  };

  // Handle confirmation for Send to Lab
  const handleSendToLabConfirmation = () => {
    setSendToLabConfirmDialogOpen(true);
  };

  // Handle confirmation for Send to NABL
  const handleSendToNABLConfirmation = () => {
    setSendToNABLConfirmDialogOpen(true);
  };

  // Handle confirmed Send to Lab action
  const handleConfirmedSendToLab = async () => {
    setSendToLabConfirmDialogOpen(false);
    await handleSendToLabFromNC();
  };

  // Handle confirmed Send to NABL action
  const handleConfirmedSendToNABL = async () => {
    setSendToNABLConfirmDialogOpen(false);
    await handleSendToNABLFromNC();
  };

  // Handle Accept Declaration
  const handleAcceptDeclaration = async () => {
    if (!declarationChecked) {
      showSnackbar("Please check the declaration checkbox", "warning");
      return;
    }

    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setAcceptingDeclaration(true);
    try {
      const payload = {
        appId: String(appId),
        assessmentId: String(labData.assessmentId),
        assessorId: String(assessorId),
        Declaration: 1,
      };

      const url = `${process.env.REACT_APP_API_URL}/assessment/assessor-accept-declaration`;
      const response = await axios.post(url, payload);
      if (response.data.success) {
        showSnackbar(
          response.data.message || "Declaration accepted successfully",
          "success",
        );
        await fetchLabDetails(); // Refresh data to update acceptDeclaration status
      } else {
        throw new Error(
          response.data.message || "Failed to accept declaration",
        );
      }
    } catch (error) {
      console.error("❌ Error accepting declaration:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error accepting declaration",
        "error",
      );
    } finally {
      setAcceptingDeclaration(false);
    }
  };

  // Save NC Remarks
  const saveNCRemarks = async (ncId, remarks) => {
    if (!ncId || !remarks.trim()) {
      showSnackbar("Please enter remarks before saving", "error");
      return;
    }

    // Find the current NC to check if it has existing assessorRemarks
    const currentNC = rejectedNCs.find((nc) => nc.id === ncId);
    const isUpdate = currentNC?.assessorRemarks;

    setSavingRemarks((prev) => ({ ...prev, [ncId]: true }));

    try {
      const payload = {
        assessorRemarks: remarks.trim(),
        assessorRemarksBy: String(assessorId),
        assessorRemarksDate: new Date().toISOString(),
      };

      const url = `${process.env.REACT_APP_API_URL}/nc-main/assessor-update/${ncId}`;
      const response = await axios.put(url, payload);

      if (response.data.success || response.data.isstatus) {
        showSnackbar(
          `Assessor remarks ${isUpdate ? "updated" : "saved"} successfully`,
          "success",
        );
        // Don't clear the input for updates, only for new saves
        if (!isUpdate) {
          setNcRemarks((prev) => ({ ...prev, [ncId]: "" }));
        }
        // Refresh NC data to show updated remarks
        await fetchNonConformitiesAll();
        window.location.reload();
      } else {
        throw new Error(
          response.data.message ||
            `Failed to ${isUpdate ? "update" : "save"} remarks`,
        );
      }
    } catch (error) {
      console.error(
        `❌ Error ${isUpdate ? "updating" : "saving"} NC remarks:`,
        error,
      );
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          `Error ${isUpdate ? "updating" : "saving"} remarks`,
        "error",
      );
    } finally {
      setSavingRemarks((prev) => ({ ...prev, [ncId]: false }));
    }
  };

  // Fetch Meeting Data
  const fetchMeetingData = async (type) => {
    if (!appId || !labData.assessmentId || !assessorId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    try {
      setLoadingMeeting(true);
      setMeetingType(type);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getopeningmeeting?appid=${appId}&assessmentid=${labData.assessmentId}&assessorid=${assessorId}&type=${type}`;

      const response = await axios.get(url);

      if (response.data.isstatus) {
        const meetingArray = response.data.opening;
        if (meetingArray && meetingArray.length > 0) {
          setMeetingData(meetingArray); // Store all records
          setMeetingDialogOpen(true);
        } else {
          showSnackbar("No meeting data found", "warning");
        }
      } else {
        showSnackbar(
          response.data.message || "No meeting data found",
          "warning",
        );
      }
    } catch (error) {
      console.error("❌ Error fetching meeting data:", error);
      showSnackbar("Error loading meeting data", "error");
    } finally {
      setLoadingMeeting(false);
    }
  };

  // Load assessor allotment data using mode 5
  const loadAssessorAllotmentData = useCallback(async () => {
    try {
      // Call API to get assessor allotment details (mode 5 - AssessorAllotment_New)
      const response = await DoApiService.getAssessorAllotmentDetails9({
        mode: 64,
        appId: parseFloat(appId),
      });

      if (response.success) {
        setAssessorsData(response.data || []);
        const matchedAssessor = (response.data || []).find(
          (item) => String(item.allotedToId) === String(assessorId),
        );
        const assessorName = matchedAssessor?.assessorName || "";
        const assessorStage = matchedAssessor?.stage || "";
        console.log("Assessor Stage:", assessorStage);
        setAssessorName(assessorName);
      } else {
        throw new Error(
          response.message || "Failed to load assessor allotment details",
        );
      }
    } catch (err) {
      console.error("Load assessor allotment error:", err);
      // Set empty data on error
      setAssessorsData([]);
    }
  }, [appId]);

  const loadFinalResultData = useCallback(
    async (assessmentId) => {
      try {
        // Call API to get final result details (mode 5 - FinalResult)
        const response = await DoApiService.getFinalResultDetails({
          appId: parseFloat(appId),
          assessmentId: parseFloat(assessmentId),
        });

        if (response.success) {
          console.log("Final Result Data:", response.data[0].FinalResult);
          setFinalCheckData(response.data[0].FinalResult);
          setFinalMessage(response.data[0].FinalResult);
        } else {
          throw new Error(
            response.message || "Failed to load final result details",
          );
        }
      } catch (err) {
        console.error("Load final result error:", err);
        // Set empty data on error
        setAssessorsData([]);
      }
    },
    [appId],
  );

  // Handle view profile action
  const handleViewProfile = (assessor) => {
    navigate(
      `/main/assessor/assessor-profile?AssessorId=${encryptText(
        assessor.allotedToId,
      )}&type=${encryptText("assessor")}`,
    );
  };
  // Get capacity color for chip display
  const getCapacityColor = (capacity) => {
    const colors = {
      "Lead Assessor and Technical Assessor": "primary",
      Observer: "secondary",
      "Technical Assessor": "success",
      "Technical Expert": "warning",
      "Team Leader": "error",
    };
    return colors[capacity] || "default";
  };

  // Get status color for chip display
  const getStatusColor = (status) => {
    const colors = {
      Active: "success",
      Inactive: "error",
      Pending: "warning",
      Confirmed: "primary",
    };
    return colors[status] || "default";
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleCloseMeetingDialog = () => {
    setMeetingDialogOpen(false);
    setMeetingData(null);
    setMeetingType("");
  };
  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/laboratory/application-stage/${appId}`,
      );
      const result = await response.json();

      if (result.success) {
        setApplicationStage(result.stage);
        console.log("Application Stage:", result.stage);
      } else {
        setApplicationStage("1"); // Default to 1
      }
    } catch (error) {
      console.error("Error fetching application stage:", error);
      setApplicationStage("1"); // Default to 1
    }
  };

  useEffect(() => {
    getApplicationStage(appId);
  }, [appId]);

  useEffect(() => {
    fetchLabDetails();
    loadAssessorAllotmentData();
  }, [appId, assessorId]);

  // Fetch NC when assessmentId is available
  useEffect(() => {
    if (labData.assessmentId) {
      fetchNonConformities();
      fetchNonConformitiesAll();
      checkApplicationReviewStatus();
      loadFinalResultData(labData.assessmentId);
    }
  }, [labData.assessmentId, appId, assessorId]);

  // Auto-refresh page every 5 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchLabDetails();
      fetchNonConformities();
      fetchNonConformitiesAll();
      checkApplicationReviewStatus();
      loadFinalResultData(labData.assessmentId);
    }, 300000); // 5 minutes = 5 * 60 * 1000 = 300000 ms

    // Cleanup on unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const capacityMap = {
    1: "Lead Assessor",
    2: "Technical Assessor",
    3: "Lead & Technical Assessor",
    4: "Technical Expert",
  };

  // Helper function to get lab type name
  const getLabTypeName = (labTypeId) => {
    const labTypes = {
      "1": "Testing",
      "2": "Calibration",
      "3": "Medical",
    };
    return labTypes[labTypeId] || "Medical";
  };

  const [nonConformities, setNonConformities] = useState([]);
  const [nonConformitiesAll, setNonConformitiesAll] = useState([]);
  const [loadingNC, setLoadingNC] = useState(false);
  const [loadingNCAll, setLoadingNCAll] = useState(false);

  // Filter rejected NCs (Lab did not accept)
  const rejectedNCs = nonConformitiesAll.filter((nc) => nc.isLabAccept === "0");

  // State for managing NC remarks
  const [ncRemarks, setNcRemarks] = useState({});
  const [savingRemarks, setSavingRemarks] = useState({});

  // Populate existing assessor remarks when non conformities change
  useEffect(() => {
    const rejectedNCs = nonConformitiesAll.filter(
      (nc) => nc.isLabAccept === "0",
    );

    const existingRemarks = {};
    rejectedNCs.forEach((nc) => {
      if (nc.assessorRemarks) {
        existingRemarks[nc.id] = nc.assessorRemarks;
      }
    });

    if (Object.keys(existingRemarks).length > 0) {
      setNcRemarks((prev) => ({ ...prev, ...existingRemarks }));
    }
  }, [nonConformities]);

  const timelineStepsLA_And_LATA = [
    {
      label: "Start Assessment",
      subLabel: "(To be done from mobile app)",
      completed: labData.startAssessment,
      current: !labData.startAssessment,
      date: labData.startAssessmentDate,
      description: "Assessment process initiated",
      selfie: labData.startSelfie,
    },
    {
      label: "Opening Meeting",
      subLabel: "(To be done from mobile app)",
      completed: labData.openingMeeting,
      current: labData.startAssessment && !labData.openingMeeting,
      description: "Initial meeting with lab team",
      action: labData.openingMeeting ? () => fetchMeetingData("Opening") : null,
      actionLabel: "View",
    },
    {
      label: "Application Review",
      completed: labData.applicationReview,
      link:
        appId &&
        orgId &&
        userType &&
        labData.assessmentId &&
        labData.labType &&
        assessorId &&
        !labData.stopAssessment &&
        !labData.applicationReview
            ? `${labType === "1"
            ? "/main/testing/laboratory-detailsAssessment"
            : labType === "2"
            ? "/main/calibration/laboratory-detailsAssessment"
            : "/main/medical/laboratory-detailsAssessment"
            }?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&capacity=${encryptText(
              userType,
            )}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&labType=${encryptText(labData.labType)}&assessorId=${encryptText(
              assessorId,
            )}`
          : null,
      description: "Reviewing application documentation",
      missingSections: !labData.applicationReview ? applicationReviewMissingSections : [],
      action:
        canCompleteApplicationReview && !labData.applicationReview
          ? handleOpenCompleteReviewDialog
          : null,
      actionLabel:
        canCompleteApplicationReview && !labData.applicationReview
          ? "Complete Review"
          : null,
      actionLoading: updatingApplicationReview,
    },
    {
      label: "Enter Checklist",
      completed: labData.checklist,
      link:
        appId &&
        orgId &&
        labData.assessmentId &&
        labData.labType &&
        assessorId &&
        labData.standard &&
        userType &&
        medicalImaging &&
        !labData.stopAssessment
          ? `/main/assessor/checklist?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&labType=${encryptText(labData.labType)}&assessorId=${encryptText(
              assessorId,
            )}&standard=${encryptText(labData.standard)}&userType=${encryptText(
              userType,
            )}&medicalImaging=${encryptText(medicalImaging)}`
          : null,
      description: "Complete assessment checklist",
    },
    ...(userType == 3
      ? [
          {
            label: "Enter Test Witness",
            completed: labData.testWitness,
            link:
              appId &&
              orgId &&
              labType &&
              assessorId &&
              labData.assessmentId &&
              !labData.stopAssessment
                ? `${(labType === "2")
                ? "/main/assessor/scope-witness-calibration"
                : "/main/assessor/scope-witness"
                }?appId=${encryptAppId(
                    appId,
                  )}&orgId=${encryptText(orgId)}&labType=${encryptText(
                    labType,
                  )}&assessorId=${encryptText(
                    assessorId,
                  )}&assessmentId=${encryptText(
                    labData.assessmentId,
                  )}&standard=${encryptText(
                    labData.standard,
                  )}&userType=${encryptText(userType)}&type=${encryptText("assessor")}`
                : null,
            description: "Record test witnessing activities",
          },
          {
            label: "View Equipment Details",
            completed: labData.equipment,
            link:
              appId &&
              orgId &&
              assessorId &&
              labData.assessmentId &&
              !labData.stopAssessment
                ? `/main/assessor/equipment-details?appId=${encryptAppId(
                    appId,
                  )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
                    assessorId,
                  )}&assessmentId=${encryptText(labData.assessmentId)}`
                : null,
            description: "Review laboratory equipment information",
          },
        ]
      : []),

    {
      label: "Enter TA DA",
      completed: labData.tada,
      link:
        appId &&
        orgId &&
        assessorId &&
        labData.assessmentId &&
        !labData.stopAssessment
          ? `/main/assessor/TADA?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}`
          : null,
      description: "Travel and daily allowance claims",
    },
    {
      label: "Assessment Completed",
      subLabel: "(To be done from mobile app)",
      completed: labData.stopAssessment,
      date: labData.stopAssessmentDate,
      description:
        "Finalize assessment process completed. All non-conformities if any will be Lab for acceptance and reply.",
      selfie: labData.stopSelfie,
    },
    {
      label: "Enter Feedback",
      completed: labData.feedback,
      link:
        appId && orgId && assessorId && labData.assessmentId
          ? `/main/assessor/feedback?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}`
          : null,
      description: "Provide assessment feedback",
    },
    {
      label: "Create NAF 6",
      completed: labData.naf6,
      link:
        appId && orgId && assessorId && labData.assessmentId && labType
          ? `/main/assessor/NAF6-new?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}&labTypeId=${encryptText(labType)}`
          : null,
      description: "Generate assessment report",
    },
    {
      label: "Closing Meeting",
      subLabel: "(To be done from mobile app)",
      completed: labData.closingMeeting,
      description: "Initial meeting with lab team",
      action: labData.closingMeeting ? () => fetchMeetingData("Closing") : null,
      actionLabel: "View",
    },
  ];

  const timelineStepsTA_TE = [
    {
      label: "Start Assessment",
      subLabel: "(To be done from mobile app)",
      completed: labData.startAssessment,
      current: !labData.startAssessment,
      date: labData.startAssessmentDate,
      description: "Assessment process initiated",
      selfie: labData.startSelfie,
    },
    {
      label: "Application Review",
      completed: labData.applicationReview,
      link:
        appId &&
        orgId &&
        userType &&
        labData.assessmentId &&
        labData.labType &&
        assessorId &&
        !labData.stopAssessment &&
        !labData.applicationReview
          ? `${labType === "1"
            ? "/main/testing/disciplineAssessment"
            : labType === "2"
            ? "/main/calibration/disciplineAssessment"
            : "/main/medical/disciplineAssessment"
            }?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&capacity=${encryptText(
              userType,
            )}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&labType=${encryptText(labData.labType)}&assessorId=${encryptText(
              assessorId,
            )}`
          : null,
      description: "Reviewing application documentation",
      missingSections: !labData.applicationReview ? applicationReviewMissingSections : [],
      action:
        canCompleteApplicationReview && !labData.applicationReview
          ? handleOpenCompleteReviewDialog
          : null,
      actionLabel:
        canCompleteApplicationReview && !labData.applicationReview
          ? "Complete Review"
          : null,
      actionLoading: updatingApplicationReview,
    },
    {
      label: "Enter Checklist",
      completed: labData.checklist,
      link:
        appId &&
        orgId &&
        labData.assessmentId &&
        labData.labType &&
        assessorId &&
        labData.standard &&
        userType &&
        medicalImaging &&
        !labData.stopAssessment
          ? `/main/assessor/checklist?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&labType=${encryptText(labData.labType)}&assessorId=${encryptText(
              assessorId,
            )}&standard=${encryptText(labData.standard)}&userType=${encryptText(
              userType,
            )}&medicalImaging=${encryptText(medicalImaging)}`
          : null,
      description: "Complete assessment checklist",
    },
    {
      label: "Enter Test Witness",
      completed: labData.testWitness,
      link:
        appId &&
        orgId &&
        labType &&
        assessorId &&
        labData.assessmentId &&
        !labData.stopAssessment
          ? `${(labType === "2")
             ? "/main/assessor/scope-witness-calibration"
            : "/main/assessor/scope-witness"
             }?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&labType=${encryptText(
              labType,
            )}&assessorId=${encryptText(assessorId)}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&standard=${encryptText(labData.standard)}&userType=${encryptText(userType)}&type=${encryptText("assessor")}`
          : null,
      description: "Record test witnessing activities",
    },
    {
      label: "View Equipment Details",
      completed: labData.equipment,
      link:
        appId &&
        orgId &&
        assessorId &&
        labData.assessmentId &&
        !labData.stopAssessment
          ? `/main/assessor/equipment-details?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}`
          : null,
      description: "Review laboratory equipment information",
    },
    {
      label: "Enter Feedback",
      completed: labData.feedback,
      link:
        appId &&
        orgId &&
        assessorId &&
        labData.assessmentId &&
        !labData.stopAssessment
          ? `/main/assessor/feedback?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}`
          : null,
      description: "Provide assessment feedback",
    },
    {
      label: "Enter TA DA",
      completed: labData.tada,
      link:
        appId &&
        orgId &&
        assessorId &&
        labData.assessmentId &&
        !labData.stopAssessment
          ? `/main/assessor/TADA?appId=${encryptAppId(
              appId,
            )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
              assessorId,
            )}&assessmentId=${encryptText(labData.assessmentId)}`
          : null,
      description: "Travel and daily allowance claims",
    },
    {
      label: "Assessment Completed",
      subLabel: "(To be done from mobile app)",
      completed: labData.stopAssessment,
      date: labData.stopAssessmentDate,
      description:
        "Finalize assessment process completed. All non-conformities if any will be Lab for acceptance and reply.",
      selfie: labData.stopSelfie,
    },
  ];

  const timelineSteps =
    userType === "1" || userType === "3"
      ? timelineStepsLA_And_LATA
      : timelineStepsTA_TE;

  const statsCards = [
    {
      title: "Capacity",
      value: labData.capacity,
      icon: <PeopleIcon />,
      color: "success",
      showCapacityChip: true,
    },
    {
      title: "Assessment Date",
      value: `${labData.assessmentDateFrom} - ${labData.assessmentDateTo}`,
      icon: <EventIcon />,
      color: "warning",
    },
    {
      title: "Application Status",
      value: labData.applicationStatus,
      icon: <PendingIcon />,
      color: "secondary",
    },
    {
      title: "Current Activity",
      value: labData.currentActivity,
      icon: <EditCalendarIcon />,
      color: "error",
    },
    {
      title: "Peer Team",
      value: "View Team",
      icon: <TeamIcon />,
      color: "primary",
      link: `/main/assessor/form9?appId=${encryptAppId(
        appId,
      )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
        assessorId,
      )}&assessmentId=${encryptText(labData.assessmentId)}&type=${encryptText(
        "assessor",
      )}}`,
    },
    {
      title: "NAF 1",
      value: isNaf1Uploaded
        ? "View NAF1"
        : userType === "1" || userType === "3"
          ? "Create NAF1"
          : "View NAF1",
      icon: <UploadIcon />,
      color: "success",
      ...(isNaf1Uploaded
        ? {
            action: true,
            onClick: () => {
              if (naf1File) {
                window.open(naf1File, "_blank");
              }
            },
          }
        : userType === "1" || userType === "3"
          ? {
              link: `/main/assessor/naf1?appId=${encryptAppId(
                appId,
              )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
                assessorId,
              )}&assessmentId=${encryptText(labData.assessmentId)}&labTypeId=${encryptText(labType)}`,
            }
          : {
              action: true,
              onClick: () => {
                if (naf1File) {
                  window.open(naf1File, "_blank");
                }
              },
            }),
    },
    {
      title: "Assessor Current Location",
      value: labData.appAddress,
      icon: <LocationIcon />,
      color: "warning",
      showAttendanceChip: true,
    },
    {
      title: "Additional Documents",
      value: "View Documents",
      icon: <AssignmentIcon />,
      color: "secondary",
      link:
        appId && orgId
          ? `/main/application/additional-documents?type=${encryptText(
              "assessor",
            )}&appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
              labData.assessmentId,
            )}&assessorId=${encryptText(assessorId)}`
          : null,
    },
  ];

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleOpenContactDialog = () => setOpenContactDialog(true);
  const handleCloseContactDialog = () => setOpenContactDialog(false);

  const handleOpenOfficerDialog = async () => {
    setLoadingOfficer(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/do/allotment-details`,
        {
          params: {
            mode: 31,
            appId: appId,
          },
        },
      );
      if (response.data.success && response.data.data.length > 0) {
        setDealingOfficer(response.data.data[0]);
        setOpenOfficerDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: "No dealing officer assigned",
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Error fetching dealing officer:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch dealing officer details",
        severity: "error",
      });
    } finally {
      setLoadingOfficer(false);
    }
  };

  const handleCloseOfficerDialog = () => setOpenOfficerDialog(false);

  // Handle View Map
  const handleViewMap = () => {
    if (labData.labLatitude && labData.labLongitude) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${labData.labLatitude},${labData.labLongitude}`,
        "_blank",
      );
    }
  };

  // Fetch Notes from API
  const fetchNotes = async () => {
    if (!appId || !labData.assessmentId || !assessorId) {
      return;
    }

    try {
      setLoadingNotes(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getnotes`;
      const params = {
        appid: appId,
        assessmentid: labData.assessmentId,
        assessorid: assessorId,
      };

      const response = await axios.get(url, { params });

      if (response.data && response.data.notes) {
        setNotes(response.data.notes || "");
      }
    } catch (error) {
      console.error("❌ Error fetching notes:", error);
      if (error.response?.status !== 404) {
        showSnackbar("Error loading notes", "error");
      }
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleNotesOpen = () => {
    setNotesOpen(true);
    fetchNotes();
  };

  const handleNotesClose = () => {
    setNotesOpen(false);
  };

  const handleNotesSave = async () => {
    if (!appId || !labData.assessmentId || !assessorId || !orgId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    try {
      setLoadingNotes(true);
      const payload = {
        appid: String(appId),
        assessmentid: String(labData.assessmentId),
        assessorid: String(assessorId),
        orgid: String(orgId),
        notes: notes || "",
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Savenotes`;
      const response = await axios.post(url, payload);

      if (response.data.success || response.data.isstatus) {
        showSnackbar("Notes saved successfully", "success");
        setNotesOpen(false);
      } else {
        throw new Error(response.data.message || "Failed to save notes");
      }
    } catch (error) {
      console.error("❌ Error saving notes:", error);
      showSnackbar(
        error.response?.data?.message || error.message || "Error saving notes",
        "error",
      );
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleStepClick = (e, step) => {
    // Check if step requires assessment to be started
    const requiresAssessmentStart = [
      "Application Review",
      "Enter Checklist",
      "Enter Test Witness",
      "Enter Equipment Details",
      "Create NAF 6",
      "Enter TA DA",
    ].includes(step.label);

    if (requiresAssessmentStart && !labData.startAssessment) {
      e.preventDefault();
      showSnackbar(
        "Please start the assessment from the mobile app first before accessing this section",
        "warning",
      );
    }
  };

  // Fetch Hourly Attendance Images
  const fetchAttendanceImages = async () => {
    if (!assessorId || !labData.assessmentId || !appId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    setLoadingAttendance(true);
    setAttendanceDialogOpen(true);

    try {
      const docId = 300; // Document ID for hourly attendance
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetImageByDocId?assessorId=${assessorId}&assessmentId=${labData.assessmentId}&appId=${appId}&docId=${docId}&sourceId=${docId}`;

      const response = await axios.get(url);

      if (response.data.isstatus && Array.isArray(response.data.img)) {
        setAttendanceImages(response.data.img);
      } else {
        setAttendanceImages([]);
      }
    } catch (error) {
      console.error("❌ Error fetching attendance images:", error);
      setAttendanceImages([]);
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Close Attendance Dialog
  const handleCloseAttendanceDialog = () => {
    setAttendanceDialogOpen(false);
    setAttendanceImages([]);
  };

  // Lightbox handlers
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Trail handlers
  const handleViewTrail = (nc) => {
    setSelectedTrailData(nc);
    setTrailDialogOpen(true);
  };

  const handleCloseTrailDialog = () => {
    setTrailDialogOpen(false);
    setSelectedTrailData(null);
  };

  // Clarification handlers
  const handleOpenClarification = (nc) => {
    setSelectedNcForClarification(nc);
    setClarificationRemarks("");
    setClarificationDialogOpen(true);
  };

  const handleCloseClarification = () => {
    setClarificationDialogOpen(false);
    setSelectedNcForClarification(null);
    setClarificationRemarks("");
  };

  const handleSendClarification = async () => {
    if (!clarificationRemarks.trim() || !selectedNcForClarification) {
      showSnackbar("Please enter remarks for clarification", "error");
      return;
    }

    try {
      setSendingClarification(true);

      const payload = {
        ncId: selectedNcForClarification.id,
        replyRemarks: clarificationRemarks.trim(),
        replyById: assessorId,
        correctiveAction: "",
        correctiveActionupload: "",
        rootcause: "",
        rootcauseupload: "",
        replyby: "Assessor",
      };

      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ncreply`;
      const response = await axios.post(url, payload);

      if (response.data.isstatus) {
        // First API call successful, now update NC status
        try {
          const updateStatusPayload = {
            Ncid: selectedNcForClarification.id,
            Status: clarificationType,
            stage: "2",
          };

          const updateStatusUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Updatencstatus?${new URLSearchParams(updateStatusPayload).toString()}`;
          const updateResponse = await axios.put(updateStatusUrl);

          if (updateResponse.data.isstatus) {
            showSnackbar("Clarification sent to CAB successfully", "success");
            handleCloseClarification();
            // Refresh NC data
            fetchNonConformities();
            fetchNonConformitiesAll();
          } else {
            showSnackbar(
              "Clarification sent but failed to update status",
              "warning",
            );
            handleCloseClarification();
            // Still refresh data as the main action was successful
            fetchNonConformities();
            fetchNonConformitiesAll();
          }
        } catch (statusError) {
          console.error("❌ Error updating NC status:", statusError);
          showSnackbar(
            "Clarification sent but failed to update status",
            "warning",
          );
          handleCloseClarification();
          // Still refresh data as the main action was successful
          fetchNonConformities();
          fetchNonConformitiesAll();
        }
      } else {
        showSnackbar("Failed to send clarification", "error");
      }
    } catch (error) {
      console.error("❌ Error sending clarification:", error);
      showSnackbar("Error sending clarification", "error");
    } finally {
      setSendingClarification(false);
    }
  };

  // ESC key handler for lightbox
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && lightboxOpen) {
        handleCloseLightbox();
      }
    };

    if (lightboxOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [lightboxOpen]);

  const getStepIcon = (step, index) => {
    if (step.current) {
      return (
        <Avatar
          sx={{
            bgcolor: colors.primary.main,
            color: "white",
            width: 32,
            height: 32,
            boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.2)}`,
          }}
        >
          {index + 1}
        </Avatar>
      );
    }
    if (step.completed) {
      return (
        <Avatar
          sx={{
            bgcolor: colors.success.main,
            color: "white",
            width: 32,
            height: 32,
          }}
        >
          <CheckCircleIcon fontSize="small" />
        </Avatar>
      );
    }
    return (
      <Avatar
        sx={{
          bgcolor: colors.grey[300],
          color: colors.grey[600],
          width: 32,
          height: 32,
        }}
      >
        {index + 1}
      </Avatar>
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: colors.grey[50],
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${colors.grey[50]} 0%, ${colors.grey[100]} 100%)`,
      }}
    >
      {/* API Error Full Page Message */}
      {apiError ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            textAlign: "center",
            px: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 6,
              borderRadius: 4,
              maxWidth: 600,
              background: "white",
              border: `2px solid ${colors.error.main}`,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${colors.error.main}, ${colors.error.light})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <WarningIcon sx={{ fontSize: 48, color: "white" }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.error.main,
                mb: 2,
              }}
            >
              Failed to Load Assessment Details
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: colors.grey[700],
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              We couldn't retrieve the lab details from the server. This could
              be due to network issues.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setApiError(false);
                  fetchLabDetails();
                }}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                  boxShadow: `0 4px 16px ${alpha(colors.primary.main, 0.3)}`,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    boxShadow: `0 8px 24px ${alpha(colors.primary.main, 0.4)}`,
                  },
                }}
              >
                Retry
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => window.history.back()}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  borderColor: colors.grey[400],
                  color: colors.grey[700],
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: colors.grey[600],
                    bgcolor: colors.grey[50],
                  },
                }}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (
        <>
          {/* Modern Header with Gradient Background */}
          <PageTitle title="NABL | Assessment Dashboard" />
          <Paper
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
              color: "white",
              borderRadius: 0,
              mb: 3,
            }}
          >
            <Box sx={{ px: 3, py: 3 }}>
              {/* Header Content */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      mr: 2,
                      width: 56,
                      height: 56,
                    }}
                  >
                    <ScienceIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {labData.labName}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                        {labData.labAddress}
                      </Typography>
                      {labData.labAddress && (
                        <IconButton
                          size="small"
                          onClick={handleViewMap}
                          sx={{
                            color: "white",
                            bgcolor: "rgba(255,255,255,0.1)",
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.2)",
                            },
                            ml: 1,
                          }}
                          title="View on Map"
                        >
                          <MapIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>

                    <Box
                      sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      <Chip
                        label={`Lab ID: ${labData.labId}`}
                        variant="outlined"
                        size="small"
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                        }}
                      />
                      <Chip
                        label={`Lab Type: ${labData.labType}`}
                        variant="outlined"
                        size="small"
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                        }}
                      />
                      <Chip
                        label={`Standard: ${labData.standard}`}
                        variant="outlined"
                        size="small"
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                        }}
                      />
                      <Chip
                        icon={
                          <Person2Outlined sx={{ color: "white !important" }} />
                        }
                        label="Contact Persons"
                        variant="outlined"
                        size="small"
                        onClick={handleOpenContactDialog}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderColor: "white",
                          },
                        }}
                      />
                      <Chip
                        icon={
                          <PreviewIcon sx={{ color: "white !important" }} />
                        }
                        label="Application Preview"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (labData.applicationPreviewFile) {
                            const encryptedAppIdParam = encryptAppId(appId);
                            const encryptedOrgIdParam = encryptText(orgId);
                            const encryptedObservation = encryptText("true");
                             if(labType==="1"){
                               const url = `/main/testing-preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;
                               window.open(url, "_blank", "noopener,noreferrer");
                             }else if(labType==="2"){
                               const url = `/main/calibration-preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;
                               window.open(url, "_blank", "noopener,noreferrer");
                             }else{    
                               const url = `/main/preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;
                               window.open(url, "_blank", "noopener,noreferrer");
                             }
                          }
                        }}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderColor: "white",
                          },
                        }}
                      />
                      <Chip
                        icon={<BadgeIcon sx={{ color: "white !important" }} />}
                        label="Dealing Officer"
                        variant="outlined"
                        size="small"
                        onClick={handleOpenOfficerDialog}
                        disabled={loadingOfficer}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.7)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderColor: "white",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Refresh Button */}
                  <Button
                    onClick={() => {
                      fetchLabDetails();
                      fetchNonConformities();
                      fetchNonConformitiesAll();
                      checkApplicationReviewStatus();
                      loadFinalResultData(labData.assessmentId);
                    }}
                    disabled={refreshing}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.2)",
                      },
                    }}
                  >
                    <RefreshIcon
                      sx={{
                        animation: refreshing
                          ? "spin 1s linear infinite"
                          : "none",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Refresh
                  </Button>

                  <Button
                    variant="outlined"
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={handleExpandClick}
                    sx={{
                      borderColor: "rgba(255,255,255,0.3)",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    {expanded ? "Collapse" : "Expand"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Enhanced Stats Grid */}
          <Collapse in={expanded}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {statsCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    onClick={
                      card.action && !card.link ? card.onClick : undefined
                    }
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: "white",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      border: `1px solid ${colors.grey[200]}`,
                      cursor: card.action || card.link ? "pointer" : "default",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                        "& .card-content": {
                          transform: "translateY(-4px)",
                        },
                      },
                    }}
                  >
                    <CardContent
                      className="card-content"
                      sx={{ p: 3, transition: "transform 0.3s ease" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            background: `linear-gradient(135deg, ${
                              colors[card.color]?.main
                            }, ${colors[card.color]?.light})`,
                            color: "white",
                            borderRadius: 2,
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 8px 20px ${alpha(
                              colors[card.color]?.main || colors.primary.main,
                              0.3,
                            )}`,
                          }}
                        >
                          {card.icon}
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: colors.grey[600],
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {card.title}
                      </Typography>

                      {card.link ? (
                        <Typography
                          variant="h6"
                          component="a"
                          href={card.link}
                          target="_blank"
                          sx={{
                            color:
                              colors[card.color]?.main || colors.primary.main,
                            textDecoration: "none",
                            cursor: "pointer",
                            display: "block",
                            fontWeight: 700,
                            fontSize: "1rem",
                            lineHeight: 1.3,
                            transition: "color 0.2s ease",
                            "&:hover": {
                              color:
                                colors[card.color]?.dark || colors.primary.dark,
                            },
                          }}
                        >
                          {card.value}
                        </Typography>
                      ) : card.action ? (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color:
                              colors[card.color]?.main || colors.primary.main,
                            fontSize: "1rem",
                            lineHeight: 1.3,
                            cursor: "pointer",
                            transition: "color 0.2s ease",
                            "&:hover": {
                              color:
                                colors[card.color]?.dark || colors.primary.dark,
                            },
                          }}
                        >
                          {card.value}
                        </Typography>
                      ) : (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: colors.grey[900],
                            fontSize: "1rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {card.value}
                        </Typography>
                      )}

                      {card.subValue && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            color: colors.grey[500],
                            fontSize: "0.85rem",
                          }}
                        >
                          {card.subValue}
                        </Typography>
                      )}

                      {card.showAttendanceChip && (
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            icon={<ClockIcon />}
                            label="View Hourly Attendance"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchAttendanceImages();
                            }}
                            sx={{
                              cursor: "pointer",
                              bgcolor: alpha(colors.warning.main, 0.1),
                              color: colors.warning.main,
                              fontWeight: 600,
                              "&:hover": {
                                bgcolor: alpha(colors.warning.main, 0.2),
                              },
                            }}
                          />
                        </Box>
                      )}
                      {card.showCapacityChip && (
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            icon={<PersonIcon />}
                            label={assessorName}
                            size="small"
                            sx={{
                              cursor: "pointer",
                              bgcolor: alpha(colors.success.main, 0.1),
                              color: colors.success.main,
                              fontWeight: 600,
                              "&:hover": {
                                bgcolor: alpha(colors.success.main, 0.2),
                              },
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Collapse>

          {/* Main Content */}
          <Grid container spacing={3}>
            {/* Working Space Section */}
            {applicationStage === "21" && assessmentStage === "2" ? null : (
              <Grid item xs={12} lg={8}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
                    background: "white",
                    border: `1px solid ${colors.grey[200]}`,
                    height: "100%",
                  }}
                >
                  {/* ================= HEADER ================= */}
                  <CardHeader
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <AssignmentIcon sx={{ color: colors.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Working Space
                        </Typography>

                        <Chip
                          label={`${nonConformities.length} Non-Conformities`}
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />

                        {/* No NC Observed */}
                        {nonConformities.length === 0 &&
                          labData?.checklist &&
                          noNc === 0 && (
                            <Button
                              variant="contained"
                              onClick={handleNoNCObserved}
                              sx={{
                                ml: "auto",
                                background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                borderRadius: 3,
                                textTransform: "none",
                              }}
                            >
                              No Non-Conformities Observed
                            </Button>
                          )}
                        {/* Action Buttons */}
                        {(userType === "1" || userType === "3") && (
                          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                            {labType === "2" && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                window.open(
                                  `/main/assessor/form73-calibration?appId=${encryptAppId(
                                    appId,
                                  )}&assessmentId=${encryptText(
                                    labData.assessmentId,
                                  )}&assessorId=${encryptText(assessorId)}`,
                                  "_blank",
                                )
                              }
                            >
                              Form 73
                            </Button>
                            )}

                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                window.open(
                                  `/main/assessor/form217-preview?appId=${encryptAppId(
                                    appId,
                                  )}&assessmentId=${encryptText(
                                    labData.assessmentId,
                                  )}&assessorId=${encryptText(assessorId)}`,
                                  "_blank",
                                )
                              }
                            >
                              View Report
                            </Button>

                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                window.open(
                                  `/main/assessor/nc-report?appId=${encryptAppId(
                                    appId,
                                  )}&assessmentId=${encryptText(
                                    labData.assessmentId,
                                  )}&assessorId=${encryptText(assessorId)}`,
                                  "_blank",
                                )
                              }
                            >
                              View NC Report
                            </Button>

                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                window.open(
                                  `/main/assessor/nc-application-finding?appId=${encryptAppId(
                                    appId,
                                  )}&assessmentId=${encryptText(
                                    labData.assessmentId,
                                  )}&orgId=${encryptText(orgId)},
                                  &labTypeId=${encryptText(labType)},
                                  &userType=${encryptText(userType)}`,
                                  "_blank",
                                )
                              }
                            >
                              Application Finding
                            </Button>
                            
                            {applicationStage === "20" && (
                              <>
                                {finalCheckData === "True" ? (
                                  feedback === "False" ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ ml: 2, color: colors.warning.main, fontWeight: 'bold' }}
                                    >
                                      Feedback Needs to be Filled
                                    </Typography>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() =>
                                        setSendToNABLDialogOpen(true)
                                      }
                                      disabled={sendingToNABL}
                                    >
                                      {sendingToNABL ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress
                                            size={14}
                                            sx={{ color: "white" }}
                                          />
                                          Sending...
                                        </Box>
                                      ) : (
                                        "Send to NABL"
                                      )}
                                    </Button>
                                  )
                                ) : (
                                  <>
                                    {feedback === "False" ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          ml: 2,
                                          color: colors.warning.main,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Feedback Needs to be Filled
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{ ml: 2, color: colors.error.main, fontWeight: 'bold' }}
                                      >
                                        {finalMessage || "Please complete all required steps"}
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                        {(userType === "2" || userType === "4") && (
                          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                window.open(
                                  `/main/assessor/nc-application-finding?appId=${encryptAppId(
                                    appId,
                                  )}&assessmentId=${encryptText(
                                    labData.assessmentId,
                                  )}&assessorId=${encryptText(
                                    assessorId,
                                  )}&orgId=${encryptText(orgId)}
                                &userType=${encryptText(userType)}`,
                                  "_blank",
                                )
                              }
                            >
                              Application Finding
                            </Button>
                          </Box>
                        )}
                        {applicationStage === "21" && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() =>
                              window.open(
                                `/main/assessor/assessor-nc-clarification?appId=${encryptAppId(
                                  appId,
                                )}&orgId=${encryptText(orgId)}&assessorId=${encryptText(
                                  assessorId,
                                )}&assessmentId=${encryptText(
                                  labData.assessmentId,
                                )}`,
                                "_blank",
                              )
                            }
                          >
                            Do Clarification
                          </Button>
                        )}
                      </Box>
                    }
                  />

                  {/* ================= CONTENT ================= */}
                  <CardContent sx={{ p: 2, maxHeight: 600, overflow: "auto" }}>
                    {/* NC Action Buttons - Show based on isclsosed status AND last reply from Assessor */}
                    {(checkCanShowSendToNABLButton() ||
                      checkCanShowSendToLabButton()) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                          mb: 2,
                          p: 2,
                          backgroundColor: alpha(colors.primary.main, 0.05),
                          borderRadius: 2,
                          border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
                        }}
                      >
                        {/* Send to Lab Button - Show when isclsosed=false AND last reply is from Assessor */}
                        {checkCanShowSendToLabButton() && (
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleSendToLabConfirmation}
                          disabled={sendingToLabFromNC}
                          startIcon={
                            sendingToLabFromNC ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <ArrowForwardIcon />
                            )
                          }
                          sx={{
                            background: `linear-gradient(135deg, ${colors.warning.main}, ${colors.orange?.[600] || colors.warning.dark})`,
                            color: "white",
                            px: 4,
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: "1.1rem",
                            boxShadow: `0 4px 16px ${alpha(colors.warning.main, 0.3)}`,
                            "&:hover": {
                              boxShadow: `0 8px 24px ${alpha(colors.warning.main, 0.4)}`,
                              transform: "translateY(-2px)",
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[300],
                              color: colors.grey[500],
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {sendingToLabFromNC ? "Sending..." : "Send to Lab"}
                        </Button>
                        )}

                        {/* Send to NABL Button - Show when isclsosed=true AND last reply is from Assessor */}
                        {checkCanShowSendToNABLButton() && (
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handleSendToNABLConfirmation}
                            disabled={sendingToNABLFromNC}
                            startIcon={
                              sendingToNABLFromNC ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <CheckCircleIcon />
                              )
                            }
                            sx={{
                              background: `linear-gradient(135deg, ${colors.success.main}, ${colors.primary.main})`,
                              color: "white",
                              px: 4,
                              py: 1.5,
                              borderRadius: 3,
                              fontWeight: 600,
                              textTransform: "none",
                              fontSize: "1.1rem",
                              boxShadow: `0 4px 16px ${alpha(colors.success.main, 0.3)}`,
                              "&:hover": {
                                boxShadow: `0 8px 24px ${alpha(colors.success.main, 0.4)}`,
                                transform: "translateY(-2px)",
                              },
                              "&:disabled": {
                                backgroundColor: colors.grey[300],
                                color: colors.grey[500],
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {sendingToNABLFromNC
                              ? "Sending..."
                              : "Send to NABL"}
                          </Button>
                        )}
                      </Box>
                    )}

                    {loadingNC ? (
                      <Box sx={{ py: 4, textAlign: "center" }}>
                        <LinearProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading non-conformities…
                        </Typography>
                      </Box>
                    ) : nonConformities.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <InfoIcon
                          sx={{ fontSize: 48, color: colors.grey[400] }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          {noNc === 1
                            ? "No Non-Conformities Observed"
                            : "No Non-Conformities Found"}
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer
                        component={Paper}
                        sx={{ borderRadius: 3 }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <b>NC Details & CAB Response</b>
                              </TableCell>
                              <TableCell>
                                <b>Latest CAB Reply</b>
                              </TableCell>
                              <TableCell>
                                <b>Actions</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {nonConformities.map((nc, index) => (
                              <TableRow key={nc.id} hover>
                                <TableCell sx={{ maxWidth: 600 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    {/* NC Basic Information */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: colors.primary.main,
                                          }}
                                        >
                                          {`NC${String(index + 1).padStart(3, "0")}`}
                                        </Typography>
                                        {(() => {
                                          const isClosed =
                                            nc.isclsosed === true ||
                                            nc.isclsosed === "True" ||
                                            nc.isclsosed === "1" ||
                                            nc.isclsosed === 1;
                                          const isUnableToClose = nc.status === "2";
                                          if (isUnableToClose) {
                                            return (
                                              <Chip
                                                label="Unable to Close"
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(colors.warning.main, 0.15),
                                                  color: colors.warning.dark || colors.warning.main,
                                                  fontWeight: 600,
                                                  fontSize: "0.7rem",
                                                }}
                                              />
                                            );
                                          }
                                          if (isClosed) {
                                            return (
                                              <Chip
                                                label="Closed"
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(colors.success.main, 0.15),
                                                  color: colors.success.dark || colors.success.main,
                                                  fontWeight: 600,
                                                  fontSize: "0.7rem",
                                                }}
                                              />
                                            );
                                          }
                                          return (
                                            <Chip
                                              label="Open"
                                              size="small"
                                              sx={{
                                                bgcolor: alpha(colors.info?.main || "#0288d1", 0.15),
                                                color: colors.info?.dark || colors.info?.main || "#0288d1",
                                                fontWeight: 600,
                                                fontSize: "0.7rem",
                                              }}
                                            />
                                          );
                                        })()}
                                      </Box>
                                      <Chip
                                        label={`Clause ${nc.clause}`}
                                        size="small"
                                        sx={{
                                          bgcolor: alpha(
                                            colors.primary.main,
                                            0.1,
                                          ),
                                          color: colors.primary.main,
                                          fontWeight: 600,
                                          alignSelf: "flex-start",
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 1 }}
                                      >
                                        {nc.description || "-"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 1 }}
                                      >
                                        <b>Assessor Name:</b>{" "}
                                        {nc.assessorName || "-"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 1 }}
                                      >
                                        <b>NC Remarks:</b> {nc.remarks || "-"}
                                      </Typography>
                                    </Box>
                                    {/* Documents Section */}
                                    {nc.document &&
                                      nc.document.split("~").filter(Boolean)
                                        .length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: colors.grey[700],
                                              mb: 1,
                                              display: "block",
                                            }}
                                          >
                                            Documents:
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: 1,
                                            }}
                                          >
                                            {nc.document
                                              .split("~")
                                              .filter(Boolean)
                                              .map((doc, i) => {
                                                const lower = doc.toLowerCase();
                                                return (
                                                  <WorkingSpaceDocument
                                                    key={i}
                                                    doc={doc}
                                                    index={i}
                                                    isImage={lower.match(
                                                      /\.(jpg|jpeg|png|gif|webp)$/,
                                                    )}
                                                    isPdf={lower.endsWith(
                                                      ".pdf",
                                                    )}
                                                    isExcel={lower.match(
                                                      /\.(xls|xlsx)$/,
                                                    )}
                                                    colors={colors}
                                                    onImageClick={
                                                      handleOpenLightbox
                                                    }
                                                  />
                                                );
                                              })}
                                          </Box>
                                        </Box>
                                      )}

                                    {/* CAB Response Section */}
                                    <Box
                                      sx={{
                                        p: 2,
                                        backgroundColor: colors.grey[50],
                                        borderRadius: 2,
                                        borderLeft: `4px solid ${colors.success.main}`,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 600,
                                          color: colors.success.dark,
                                          mb: 2,
                                        }}
                                      >
                                        CAB Response & Documents
                                      </Typography>

                                      {/* Corrective Action Section */}
                                      {nc.cabReply && nc.cabReply !== "-" && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: colors.grey[700],
                                              mb: 1,
                                              display: "block",
                                            }}
                                          >
                                            Proposed Corrective Action (CAB):
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              p: 1.5,
                                              backgroundColor: colors.white,
                                              borderRadius: 1,
                                              border: `1px solid ${colors.success.light}`,
                                            }}
                                          >
                                            {nc.cabReply}
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Reply Date Section */}
                                      {nc.cabReplyDate && (
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: colors.grey[700],
                                              mb: 0.5,
                                              display: "block",
                                            }}
                                          >
                                            Reply Date:
                                          </Typography>
                                          <Chip
                                            label={new Date(nc.cabReplyDate)
                                              .toLocaleString("en-GB", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: true,
                                              })
                                              .replace(",", "")}
                                            size="small"
                                            sx={{
                                              bgcolor: alpha(
                                                colors.success.main,
                                                0.1,
                                              ),
                                              color: colors.success.main,
                                              fontWeight: 600,
                                              fontSize: "0.75rem",
                                            }}
                                          />
                                        </Box>
                                      )}

                                      {/* Show message when no CAB response */}
                                      {(!nc.cabReply || nc.cabReply === "-") &&
                                        !nc.cabReplyDate &&
                                        (!nc.document ||
                                          nc.document.split("~").filter(Boolean)
                                            .length === 0) && (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: colors.grey[500],
                                              fontStyle: "italic",
                                            }}
                                          >
                                            No CAB response received yet
                                          </Typography>
                                        )}
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Latest CAB Reply Column */}
                                <TableCell sx={{ maxWidth: 400 }}>
                                  {nc.ncreply && nc.ncreply.length > 0 ? (
                                    (() => {
                                      // Filter replies to only show CAB or LAB replies
                                      const cabLabReplies = nc.ncreply.filter(
                                        (reply) =>
                                          reply.replyby &&
                                          (reply.replyby.toLowerCase() ===
                                            "cab" ||
                                            reply.replyby.toLowerCase() ===
                                              "lab"),
                                      );

                                      if (cabLabReplies.length === 0) {
                                        return (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: colors.grey[500],
                                              fontStyle: "italic",
                                              textAlign: "center",
                                              py: 2,
                                            }}
                                          >
                                            No CAB/LAB reply received yet
                                          </Typography>
                                        );
                                      }

                                      const latestReply =
                                        cabLabReplies[cabLabReplies.length - 1]; // Get latest CAB/LAB reply
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              fontWeight: 600,
                                              color: colors.warning.dark,
                                              mb: 1,
                                            }}
                                          >
                                            Latest CAB Reply
                                          </Typography>

                                          {/* Root Cause & Document by CAB */}
                                          {(latestReply.rootcause ||
                                            latestReply.rootcauseupload) && (
                                            <Box sx={{ mb: 2 }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: colors.grey[700],
                                                  mb: 1,
                                                  display: "block",
                                                }}
                                              >
                                                Root Cause & Document By CAB:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 1,
                                                }}
                                              >
                                                {latestReply.rootcause && (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      p: 1,
                                                      backgroundColor: alpha(
                                                        colors.warning.main,
                                                        0.1,
                                                      ),
                                                      borderRadius: 1,
                                                      borderLeft: `3px solid ${colors.warning.main}`,
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {latestReply.rootcause}
                                                  </Typography>
                                                )}
                                                {latestReply.rootcauseupload && (
                                                  <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 0.5,
                                                      }}
                                                    >
                                                      Supporting Document:
                                                    </Typography>
                                                    <WorkingSpaceDocument
                                                      doc={
                                                        latestReply.rootcauseupload
                                                      }
                                                      index={0}
                                                      isImage={latestReply.rootcauseupload
                                                        .toLowerCase()
                                                        .match(
                                                          /\.(jpg|jpeg|png|gif|webp)$/,
                                                        )}
                                                      isPdf={latestReply.rootcauseupload
                                                        .toLowerCase()
                                                        .endsWith(".pdf")}
                                                      isExcel={latestReply.rootcauseupload
                                                        .toLowerCase()
                                                        .match(/\.(xls|xlsx)$/)}
                                                      colors={colors}
                                                      onImageClick={
                                                        handleOpenLightbox
                                                      }
                                                    />
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Corrective Action & Document by CAB */}
                                          {(latestReply.correctiveAction ||
                                            latestReply.correctiveActionupload) && (
                                            <Box sx={{ mb: 2 }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: colors.grey[700],
                                                  mb: 1,
                                                  display: "block",
                                                }}
                                              >
                                                Corrective Action & Document By
                                                CAB:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 1,
                                                }}
                                              >
                                                {latestReply.correctiveAction && (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      p: 1,
                                                      backgroundColor: alpha(
                                                        colors.success.main,
                                                        0.1,
                                                      ),
                                                      borderRadius: 1,
                                                      borderLeft: `3px solid ${colors.success.main}`,
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {
                                                      latestReply.correctiveAction
                                                    }
                                                  </Typography>
                                                )}
                                                {latestReply.correctiveActionupload && (
                                                  <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                      sx={{
                                                        display: "block",
                                                        mb: 0.5,
                                                      }}
                                                    >
                                                      Supporting Document:
                                                    </Typography>
                                                    <WorkingSpaceDocument
                                                      doc={
                                                        latestReply.correctiveActionupload
                                                      }
                                                      index={0}
                                                      isImage={latestReply.correctiveActionupload
                                                        .toLowerCase()
                                                        .match(
                                                          /\.(jpg|jpeg|png|gif|webp)$/,
                                                        )}
                                                      isPdf={latestReply.correctiveActionupload
                                                        .toLowerCase()
                                                        .endsWith(".pdf")}
                                                      isExcel={latestReply.correctiveActionupload
                                                        .toLowerCase()
                                                        .match(/\.(xls|xlsx)$/)}
                                                      colors={colors}
                                                      onImageClick={
                                                        handleOpenLightbox
                                                      }
                                                    />
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Reply Date and By */}
                                          <Box
                                            sx={{
                                              display: "flex",
                                              gap: 2,
                                              alignItems: "center",
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {latestReply.insertdate && (
                                              <Chip
                                                label={`Reply: ${new Date(
                                                  latestReply.insertdate,
                                                ).toLocaleString("en-GB", {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                  hour12: true,
                                                })}`}
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(
                                                    colors.primary.main,
                                                    0.1,
                                                  ),
                                                  color: colors.primary.main,
                                                  fontWeight: 600,
                                                  fontSize: "0.75rem",
                                                }}
                                              />
                                            )}
                                            {latestReply.replyby && (
                                              <Chip
                                                label={latestReply.replyby}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      );
                                    })()
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: colors.grey[500],
                                        fontStyle: "italic",
                                        textAlign: "center",
                                        py: 2,
                                      }}
                                    >
                                      No CAB/LAB reply received yet
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() => handleViewTrail(nc)}
                                      sx={{
                                        backgroundColor: alpha(
                                          colors.primary.main,
                                          0.1,
                                        ),
                                        "&:hover": {
                                          backgroundColor: alpha(
                                            colors.primary.main,
                                            0.2,
                                          ),
                                        },
                                      }}
                                      title="View Trail"
                                    >
                                      <TimelineIcon />
                                    </IconButton>
                                    {nc.stage === "1" &&
                                      assessmentStage === "5h" && (
                                        <IconButton
                                          color="secondary"
                                          size="small"
                                          onClick={() =>
                                            handleOpenClarification(nc)
                                          }
                                          sx={{
                                            backgroundColor: alpha(
                                              colors.secondary.main,
                                              0.1,
                                            ),
                                            "&:hover": {
                                              backgroundColor: alpha(
                                                colors.secondary.main,
                                                0.2,
                                              ),
                                            },
                                          }}
                                          title="Reply to CAB for Clarification"
                                        >
                                          <ReplyIcon />
                                        </IconButton>
                                      )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            {/* Enhanced Timeline Section */}
            <Grid item xs={12} lg={4}>
              {!labData.acceptDeclaration ? (
                // Show Declaration Card if not accepted
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
                    background: "white",
                    border: `1px solid ${colors.grey[200]}`,
                    height: "100%",
                  }}
                >
                  <CardHeader
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <AssignmentIcon sx={{ color: colors.warning.main }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: colors.grey[900] }}
                        >
                          Assessment Declaration
                        </Typography>
                      </Box>
                    }
                    sx={{
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      pb: 2,
                    }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Please read and accept the declaration to proceed with
                        the assessment.
                      </Typography>
                    </Alert>

                    <Box
                      sx={{
                        p: 1,
                        bgcolor: alpha(colors.primary.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
                        mb: 2,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={declarationChecked}
                            onChange={(e) =>
                              setDeclarationChecked(e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            I declare that I have reviewed assessment related
                            documents and found the same to be
                            appropriate/adequate to conduct the assessment.
                          </Typography>
                        }
                      />
                    </Box>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                      >
                        <strong>Note:</strong> While reviewing the documents and
                        records supplied by NABL and CAB, if you observe any
                        gross anomalies, please immediately inform dealing
                        officer in NABL (through email).
                      </Typography>
                    </Alert>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleAcceptDeclaration()}
                      disabled={!declarationChecked || acceptingDeclaration}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: "none",
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(colors.primary.main, 0.3)}`,
                        "&:hover": {
                          boxShadow: `0 6px 16px ${alpha(colors.primary.main, 0.4)}`,
                        },
                        "&:disabled": {
                          background: colors.grey[300],
                        },
                      }}
                    >
                      {acceptingDeclaration ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ mr: 1, color: "white" }}
                          />
                          Accepting...
                        </>
                      ) : (
                        "Accept Declaration"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                // Show Assessment Progress if declaration is accepted
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
                    background: "white",
                    border: `1px solid ${colors.grey[200]}`,
                    height: "100%",
                  }}
                >
                  <CardHeader
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <TrendingUpIcon sx={{ color: colors.primary.main }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: colors.grey[900] }}
                          >
                            Assessment Progress
                          </Typography>
                        </Box>
                        <Button
                          onClick={() => {
                            fetchLabDetails();
                            fetchNonConformities();
                            fetchNonConformitiesAll();
                            checkApplicationReviewStatus();
                            loadFinalResultData(labData.assessmentId);
                          }}
                          disabled={refreshing}
                          size="small"
                          sx={{
                            "&:hover": {
                              bgcolor: alpha(colors.primary.main, 0.1),
                            },
                          }}
                        >
                          <RefreshIcon
                            sx={{
                              color: colors.primary.main,
                              fontSize: 20,
                              animation: refreshing
                                ? "spin 1s linear infinite"
                                : "none",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                          Refresh
                        </Button>
                      </Box>
                    }
                    sx={{
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      pb: 2,
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ maxHeight: 500, overflow: "auto", pr: 1 }}>
                      {timelineSteps.map((step, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: 0.5,
                            p: 1.5,
                            borderRadius: 3,
                            backgroundColor: step.current
                              ? alpha(colors.primary.main, 0.05)
                              : "transparent",
                            border: step.current
                              ? `2px solid ${alpha(colors.primary.main, 0.2)}`
                              : "none",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: alpha(colors.primary.main, 0.03),
                            },
                          }}
                        >
                          <Box sx={{ mr: 2, flexShrink: 0 }}>
                            {getStepIcon(step, index)}
                          </Box>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: step.current
                                    ? colors.primary.main
                                    : step.completed
                                      ? colors.success.main
                                      : colors.grey[700],
                                  mb: step.subLabel ? 0 : 0.5,
                                }}
                              >
                                {step.link ? (
                                  <a
                                    href={step.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleStepClick(e, step)}
                                    style={{
                                      color: "inherit",
                                      textDecoration: "none",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    {step.label}
                                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                  </a>
                                ) : (
                                  step.label
                                )}
                              </Typography>
                              {step.subLabel && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: colors.grey[500],
                                    fontStyle: "italic",
                                    fontSize: "0.7rem",
                                    mb: 0.5,
                                    display: "block",
                                  }}
                                >
                                  {step.subLabel}
                                </Typography>
                              )}
                            </Box>
                            {step.description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.grey[600],
                                  fontSize: "0.8rem",
                                  mb: 1,
                                }}
                              >
                                {step.description}
                              </Typography>
                            )}
                            {step.missingSections &&
                              step.missingSections.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  {step.missingSections.map((section, i) => (
                                    <Typography
                                      key={i}
                                      variant="caption"
                                      sx={{
                                        color: colors.error.main,
                                        display: "block",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {section} is Pending
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            {step.date && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.grey[500],
                                  fontWeight: 500,
                                  display: "block",
                                  mb: step.selfie ? 1 : 0,
                                }}
                              >
                                {step.date}
                              </Typography>
                            )}
                            {step.selfie && (
                              <Box sx={{ mt: 1 }}>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 2,
                                    bgcolor: alpha(colors.primary.main, 0.05),
                                    border: `1px solid ${alpha(
                                      colors.primary.main,
                                      0.2,
                                    )}`,
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      bgcolor: alpha(colors.primary.main, 0.1),
                                      transform: "scale(1.02)",
                                      boxShadow: 2,
                                    },
                                  }}
                                  onClick={() =>
                                    handleOpenLightbox(step.selfie)
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: 1,
                                      overflow: "hidden",
                                      border: `2px solid ${colors.primary.main}`,
                                    }}
                                  >
                                    <img
                                      src={step.selfie}
                                      alt="Selfie"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: colors.primary.main,
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <CameraAltIcon sx={{ fontSize: 14 }} />
                                      View Selfie
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                            {step.action && step.actionLabel && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={step.action}
                                  disabled={loadingMeeting}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    px: 2,
                                    py: 0.5,
                                    borderColor: colors.primary.main,
                                    color: colors.primary.main,
                                    "&:hover": {
                                      borderColor: colors.primary.dark,
                                      bgcolor: alpha(colors.primary.main, 0.05),
                                    },
                                  }}
                                >
                                  {step.actionLabel}
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Lab Disagreed NCs Section */}
            {(userType === "1" || userType === "3") &&
              applicationStage === "20" && (
                <Grid item xs={12}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
                      background: "white",
                      border: `2px solid ${colors.error.light}`,
                    }}
                  >
                    <CardHeader
                      title={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 3 }}
                        >
                          <ErrorIcon sx={{ color: colors.error.main }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: colors.error.main }}
                          >
                            Lab Disagreed Non-Conformities
                          </Typography>
                          <Chip
                            label={`${rejectedNCs.length} Disagreed`}
                            color="error"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      }
                      sx={{
                        backgroundColor: alpha(colors.error.main, 0.05),
                        borderBottom: `1px solid ${colors.error.light}`,
                        pb: 2,
                      }}
                    />
                    <CardContent
                      sx={{ p: 3, maxHeight: "600px", overflow: "auto" }}
                    >
                      {rejectedNCs.map((nc, index) => (
                        <Paper
                          key={nc.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: 3,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                            border: `1px solid ${colors.error.light}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: colors.error.main,
                                  }}
                                >
                                  {`NC${String(index + 1).padStart(3, "0")}`}
                                </Typography>
                                <Chip
                                  label={`Clause ${nc.clause}`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(colors.error.main, 0.1),
                                    color: colors.error.main,
                                    fontWeight: 600,
                                  }}
                                />
                                <Chip
                                  label="Lab Disagreed"
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>

                              {nc.description && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: colors.grey[800],
                                    fontWeight: 500,
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: colors.grey[50],
                                    borderRadius: 2,
                                    borderLeft: `4px solid ${colors.error.main}`,
                                  }}
                                >
                                  {nc.description}
                                </Typography>
                              )}

                              {nc.assessorName && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: colors.grey[600], mb: 1 }}
                                >
                                  <strong>NC Raised Assessor:</strong>{" "}
                                  {nc.assessorName}
                                </Typography>
                              )}
                              {nc.remarks && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: colors.grey[600], mb: 1 }}
                                >
                                  <strong>Existing Remarks:</strong>{" "}
                                  {nc.remarks}
                                </Typography>
                              )}

                              {/* Assessor Remarks Input Section */}
                              <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: colors.grey[700],
                                    mb: 1,
                                    fontWeight: 600,
                                  }}
                                >
                                  Add Assessor Remarks:
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <TextField
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={ncRemarks[nc.id] || ""}
                                    onChange={(e) =>
                                      setNcRemarks((prev) => ({
                                        ...prev,
                                        [nc.id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Enter your remarks for this NC..."
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        "&:hover": {
                                          "& > fieldset": {
                                            borderColor: colors.error.main,
                                          },
                                        },
                                        "&.Mui-focused": {
                                          "& > fieldset": {
                                            borderColor: colors.error.main,
                                          },
                                        },
                                      },
                                    }}
                                  />
                                  <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() =>
                                      saveNCRemarks(
                                        nc.id,
                                        ncRemarks[nc.id] || "",
                                      )
                                    }
                                    disabled={
                                      !ncRemarks[nc.id]?.trim() ||
                                      savingRemarks[nc.id]
                                    }
                                    sx={{
                                      minWidth: 100,
                                      height: 40,
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      textTransform: "none",
                                      boxShadow: `0 4px 12px ${alpha(colors.error.main, 0.3)}`,
                                      "&:hover": {
                                        boxShadow: `0 6px 16px ${alpha(colors.error.main, 0.4)}`,
                                      },
                                    }}
                                  >
                                    {savingRemarks[nc.id]
                                      ? "Saving..."
                                      : nc.assessorRemarks
                                        ? "Update"
                                        : "Save"}
                                  </Button>
                                </Box>
                              </Box>

                              {nc.document && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    mt: 2,
                                  }}
                                >
                                  {nc.document
                                    .split("~")
                                    .filter(Boolean)
                                    .map((doc, docIndex) => {
                                      const docLower = doc.toLowerCase();
                                      const isImage = docLower.match(
                                        /\.(jpg|jpeg|png|gif|webp)$/,
                                      );
                                      const isPdf = docLower.endsWith(".pdf");
                                      const isExcel =
                                        docLower.match(/\.(xlsx|xls)$/);

                                      return (
                                        <WorkingSpaceDocument
                                          key={docIndex}
                                          doc={doc}
                                          index={docIndex}
                                          isImage={isImage}
                                          isPdf={isPdf}
                                          isExcel={isExcel}
                                          colors={colors}
                                          onImageClick={
                                            isImage ? handleOpenLightbox : null
                                          }
                                        />
                                      );
                                    })}
                                </Box>
                              )}
                              {nc.cabReply && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: colors.grey[600], mb: 1 }}
                                >
                                  <strong>
                                    Corrective action proposed by the
                                    laboratory:
                                  </strong>{" "}
                                  {nc.cabReply}
                                </Typography>
                              )}
                              {nc.cabReplyDate && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: colors.grey[600], mb: 1 }}
                                >
                                  <strong>Date:</strong>{" "}
                                  {new Date(nc.cabReplyDate)
                                    .toLocaleString("en-GB", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                      hour12: true,
                                    })
                                    .replace(",", "")}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}

            {/* Assessment Schedule Table */}
            {(userType === "1" || userType === "3") && (
              <Grid item xs={12} sx={{ display: "none" }}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                  <CardHeader
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <GroupIcon sx={{ color: colors.primary.main }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: colors.grey[900] }}
                        >
                          Peer Team
                        </Typography>
                      </Box>
                    }
                    sx={{
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      pb: 2,
                    }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer
                      component={Paper}
                      sx={{ maxHeight: "600px", overflow: "auto" }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                textAlign: "center",
                                width: 80,
                              }}
                            >
                              View Profile
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                minWidth: 200,
                              }}
                            >
                              Assessment Details
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                minWidth: 120,
                              }}
                            >
                              Assessor Details
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                minWidth: 120,
                              }}
                            >
                              Organisation Details
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "primary.main",
                                color: "white",
                                minWidth: 100,
                              }}
                            >
                              Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assessorsData.map((row, index) => (
                            <TableRow
                              key={row.id || index}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: "#fafafa",
                                },
                                "&:hover": {
                                  backgroundColor: "#f0f8ff",
                                },
                              }}
                            >
                              <TableCell sx={{ textAlign: "center" }}>
                                {index + 1} <br />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewProfile(row)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    mt: 0.5,
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                      backgroundColor: "#e3f2fd",
                                      "&:hover": { backgroundColor: "#bbdefb" },
                                    }}
                                    onClick={() => {
                                      const url = `/main/assessor/assessment-dashboard?appId=${encryptAppId(
                                        appId,
                                      )}&orgId=${encryptText(
                                        orgId,
                                      )}&assessorId=${encryptText(
                                        row.allotedToId,
                                      )}`;

                                      window.open(
                                        url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                    }}
                                  >
                                    Dashboard
                                  </Button>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "medium", mb: 0.5 }}
                                  >
                                    {row.laboratoryName ||
                                      labData?.labName ||
                                      "N/A"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    Applicaiton No.:{" "}
                                    {row.applicationNo || "N/A"}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "success.main",
                                        fontWeight: "medium",
                                      }}
                                    >
                                      From:{" "}
                                      {formatDate(
                                        row.fromDate || row.startDate,
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "error.main",
                                        fontWeight: "medium",
                                      }}
                                    >
                                      To:{" "}
                                      {formatDate(row.toDate || row.endDate)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "medium", mb: 0.5 }}
                                >
                                  Name:{" "}
                                  {row.assessor || row.assessorName || "N/A"}
                                </Typography>
                                <Chip
                                  label={
                                    row.capacity ||
                                    row.assessorCapacity ||
                                    "N/A"
                                  }
                                  size="small"
                                  color={getCapacityColor(
                                    row.capacity || row.assessorCapacity,
                                  )}
                                  variant="filled"
                                />
                                <Typography variant="body2">
                                  Expertise:{" "}
                                  {row.fieldOfExpertise ||
                                    row.expertise ||
                                    "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  Location:{" "}
                                  {row.assessorLocation ||
                                    row.location ||
                                    "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {row.organisation ||
                                    row.organization ||
                                    "N/A"}
                                </Typography>
                                <Chip
                                  label={
                                    row.contact ||
                                    row.contactNumber ||
                                    row.mobile ||
                                    "N/A"
                                  }
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                ></Chip>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status || "Active"}
                                  size="small"
                                  color={getStatusColor(row.status)}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}

                          {assessorsData.length === 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={12}
                                sx={{ textAlign: "center", py: 4 }}
                              >
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                >
                                  No assessor allotment data found for this
                                  application
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Contact Person Details Dialog */}
          <Dialog
            open={openContactDialog}
            onClose={handleCloseContactDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              },
            }}
          >
            <DialogTitle
              sx={{
                pb: 2,
                pt: 3,
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                color: "white",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    p: 1,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Person2Outlined sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Contact Persons Details
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2, mt: 2 }}>
              <Grid container spacing={3}>
                {/* Contact Person Card */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      bgcolor: "#f8f9ff",
                      borderRadius: 2,
                      border: "1px solid #e3e8ff",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: colors.primary.main,
                          color: "white",
                          p: 1,
                          borderRadius: 1.5,
                          mr: 1.5,
                        }}
                      >
                        <Person2Outlined fontSize="small" />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: colors.primary.main }}
                      >
                        Contact Person
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 0.5 }}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Name
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {labData.contactPerson || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Mobile Number
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: colors.primary.main,
                          }}
                        >
                          {labData.contactMobile || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Email Address
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {labData.contactEmail || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Lab Head Card */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      bgcolor: "#fff8f0",
                      borderRadius: 2,
                      border: "1px solid #ffe8cc",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: colors.warning.main,
                          color: "white",
                          p: 1,
                          borderRadius: 1.5,
                          mr: 1.5,
                        }}
                      >
                        <Person2Outlined fontSize="small" />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: colors.warning.main }}
                      >
                        Head of Laboratory
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 0.5 }}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Name
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {labData.labhead || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Mobile Number
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {labData.labheadMobile || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          Email Address
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: colors.warning.main,
                            wordBreak: "break-word",
                          }}
                        >
                          {labData.labheadEmail || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
              <Button
                onClick={handleCloseContactDialog}
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 4,
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.secondary.dark} 100%)`,
                  },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dealing Officer Dialog */}
          <Dialog
            open={openOfficerDialog}
            onClose={handleCloseOfficerDialog}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Dealing Officer Information</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    mr: 2,
                    width: 40,
                    height: 40,
                  }}
                >
                  <BadgeIcon />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  Dealing Officer
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Officer Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, mt: 0.5 }}
                    >
                      {dealingOfficer?.name || "Not assigned"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, mt: 0.5 }}
                    >
                      {dealingOfficer?.email || "N/A"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Contact
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, mt: 0.5 }}
                    >
                      {dealingOfficer?.mobile || dealingOfficer?.email || "N/A"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseOfficerDialog}
                color="primary"
                variant="outlined"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Enhanced Notes FAB */}
          <Fab
            variant="extended"
            onClick={handleNotesOpen}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
              color: "white",
              borderRadius: 3,
              px: 3,
              boxShadow: `0 8px 24px ${alpha(colors.primary.main, 0.4)}`,
              "&:hover": {
                boxShadow: `0 12px 32px ${alpha(colors.primary.main, 0.5)}`,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <NotesIcon sx={{ mr: 1 }} />
            Quick Notes
          </Fab>

          {/* Enhanced Notes Dialog */}
          <Dialog
            open={notesOpen}
            onClose={handleNotesClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: "white",
                boxShadow: "0 24px 64px rgba(0,0,0,0.12)",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                color: "white",
                py: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <NotesIcon />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Assessment Notes
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Maintain your important notes and observations here
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 1 }}>
              {loadingNotes ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading notes...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EventIcon />}
                      onClick={() => {
                        const now = new Date();
                        const day = String(now.getDate()).padStart(2, "0");
                        const month = String(now.getMonth() + 1).padStart(
                          2,
                          "0",
                        );
                        const year = now.getFullYear();
                        let hours = now.getHours();
                        const minutes = String(now.getMinutes()).padStart(
                          2,
                          "0",
                        );
                        const ampm = hours >= 12 ? "PM" : "AM";
                        hours = hours % 12 || 12;
                        const formattedDateTime = `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
                        const currentNotes = notes.trim();
                        setNotes(
                          currentNotes
                            ? `${currentNotes}\n${formattedDateTime}\n`
                            : `${formattedDateTime}\n`,
                        );
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: colors.primary.main,
                        color: colors.primary.main,
                        "&:hover": {
                          borderColor: colors.primary.dark,
                          bgcolor: alpha(colors.primary.main, 0.05),
                        },
                      }}
                    >
                      Insert Current Date/Time
                    </Button>
                  </Box>
                  <TextField
                    multiline
                    rows={12}
                    fullWidth
                    variant="outlined"
                    label="Notes"
                    placeholder="Enter your notes, observations, and important points here..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    InputProps={{
                      endAdornment: notes ? (
                        <InputAdornment
                          position="end"
                          sx={{ alignSelf: "flex-start", mt: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              const lines = notes.trim().split("\n");
                              if (lines.length === 0) return;

                              // Find last datetime entry (format: DD-MM-YYYY H:MM AM/PM)
                              let lastDateTimeIndex = -1;
                              for (let i = lines.length - 1; i >= 0; i--) {
                                if (
                                  /^\d{2}-\d{2}-\d{4}\s+\d{1,2}:\d{2}\s+(AM|PM)$/.test(
                                    lines[i].trim(),
                                  )
                                ) {
                                  lastDateTimeIndex = i;
                                  break;
                                }
                              }

                              if (lastDateTimeIndex !== -1) {
                                // Remove datetime line and next line (note content)
                                const newLines = [...lines];
                                newLines.splice(lastDateTimeIndex, 2); // Remove datetime + note
                                setNotes(newLines.join("\n").trim());
                              }
                            }}
                            sx={{
                              color: colors.error.main,
                              "&:hover": {
                                bgcolor: alpha(colors.error.main, 0.1),
                              },
                            }}
                            title="Remove Last Entry"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        borderColor: colors.grey[300],
                        "&:hover fieldset": {
                          borderColor: colors.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button
                onClick={handleNotesClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  color: colors.grey[600],
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleNotesSave}
                variant="contained"
                disabled={loadingNotes}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  boxShadow: `0 4px 16px ${alpha(colors.primary.main, 0.3)}`,
                  "&:hover": {
                    boxShadow: `0 8px 24px ${alpha(colors.primary.main, 0.4)}`,
                  },
                }}
              >
                {loadingNotes ? "Saving..." : "Save Notes"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Enhanced NAF1 Dialog */}
          <Dialog
            open={naf1DialogOpen}
            onClose={() => setNaf1DialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: "white",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.light})`,
                color: "white",
                py: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <UploadIcon />
                  <Typography variant="h6">NAF1 Document</Typography>
                </Box>
                <IconButton
                  onClick={() => setNaf1DialogOpen(false)}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: "center", py: 3 }}>
                <a
                  href={"/NAF1-Format.docx"}
                  download="NAF1-Format.docx"
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      color: colors.primary.main,
                      borderColor: colors.primary.main,
                      boxShadow: `0 2px 8px ${alpha(
                        colors.primary.main,
                        0.08,
                      )}`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.light})`,
                        color: "white",
                      },
                    }}
                  >
                    Download NAF1 Format
                  </Button>
                </a>
                <input
                  type="file"
                  accept=".doc,.docx,.pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  id="naf1-upload"
                  onChange={(e) => {
                    setNaf1File(e.target.files[0]);
                    setIsNaf1Uploaded(true);
                  }}
                />
                <FileUploadComponent
                  fieldName="naf1File"
                  label="Upload NAF1 Document"
                  orgId={assessorId}
                  section="naf1-documents"
                  required={true}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  maxSize={5}
                  multiple={false}
                  onChange={(fieldName, fileData) => {
                    if (fileData && fileData.fileName) {
                      setNaf1File(fileData.fileName);
                      setIsNaf1Uploaded(true);
                    } else {
                      setNaf1File(null);
                      setIsNaf1Uploaded(false);
                    }
                  }}
                  initialFile={naf1File}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setNaf1DialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadNaf1}
                variant="contained"
                disabled={!naf1File}
                startIcon={<UploadIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.light})`,
                }}
              >
                Upload NAF1
              </Button>
            </DialogActions>
          </Dialog>

          {/* Meeting Dialog */}
          <Dialog
            open={meetingDialogOpen}
            onClose={handleCloseMeetingDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
              },
            }}
          >
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                color: "white",
                py: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BusinessIcon />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {meetingType} Meeting Details
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseMeetingDialog}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              {loadingMeeting ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading meeting details...
                  </Typography>
                </Box>
              ) : meetingData && meetingData.length > 0 ? (
                <Box>
                  {/* Meeting Records Table */}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: `1px solid ${colors.grey[200]}` }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: colors.grey[50] }}>
                          <TableCell
                            sx={{ fontWeight: 700, color: colors.grey[700] }}
                          >
                            Date & Time
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, color: colors.grey[700] }}
                          >
                            Lab Personal
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, color: colors.grey[700] }}
                          >
                            Designation
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {meetingData.map((meeting, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": { bgcolor: colors.grey[50] },
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {meeting.dateTimes
                                  ? (() => {
                                      const dateObj = parseDateString(
                                        meeting.dateTimes,
                                      );
                                      return dateObj && !isNaN(dateObj)
                                        ? dateObj.toLocaleString("en-GB", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                            hour12: true,
                                          })
                                        : meeting.dateTimes;
                                    })()
                                  : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {meeting.labPersonal || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {meeting.designation || "N/A"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Meeting Photographs Section */}
                  {meetingData[0]?.document &&
                    meetingData[0].document.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: colors.grey[800],
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <DescriptionIcon
                            sx={{ color: colors.primary.main }}
                          />
                          Meeting Photographs
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: `1px solid ${colors.grey[200]}`,
                            borderRadius: 2,
                            bgcolor: colors.grey[50],
                          }}
                        >
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}
                          >
                            {meetingData[0].document.map((doc, docIndex) => (
                              <MeetingImage
                                key={docIndex}
                                doc={doc}
                                index={docIndex}
                                colors={colors}
                                onImageClick={handleOpenLightbox}
                              />
                            ))}
                          </Box>
                        </Paper>
                      </Box>
                    )}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No meeting data available
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleCloseMeetingDialog}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Hourly Attendance Images Dialog */}
          <Dialog
            open={attendanceDialogOpen}
            onClose={handleCloseAttendanceDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
              },
            }}
          >
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                color: "white",
                py: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <ClockIcon />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Hourly Attendance Images
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseAttendanceDialog}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              {loadingAttendance ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading attendance images...
                  </Typography>
                </Box>
              ) : attendanceImages.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: `1px solid ${colors.grey[200]}`,
                    borderRadius: 2,
                    bgcolor: colors.grey[50],
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {attendanceImages.map((image, imgIndex) => (
                      <AttendanceImage
                        key={imgIndex}
                        doc={image}
                        index={imgIndex}
                        colors={colors}
                        onImageClick={handleOpenLightbox}
                      />
                    ))}
                  </Box>
                </Paper>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <ImageIcon
                    sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    No Attendance Images Available
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    No hourly attendance images have been uploaded yet.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{ p: 2, borderTop: `1px solid ${colors.grey[200]}` }}
            >
              <Button
                onClick={handleCloseAttendanceDialog}
                variant="contained"
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                  color: "white",
                  px: 3,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {applicationStage === "20" && (
            <HourlyAttendanceDialog
              open={attendanceCheckDialogOpen}
              onClose={() => setAttendanceCheckDialogOpen(false)}
              message={attendanceCheckMessage}
              onRecheck={() => {
                checkHourlyAttendance();
                //fetchlabstagedetails();
              }}
            />
          )}

          {applicationStage === "20" && (
            <LocationWarningDialog
              open={locationWarningOpen}
              onClose={() => setLocationWarningOpen(false)}
              distanceMessage={distanceMessage}
            />
          )}

          {/* Complete Review Confirmation Dialog */}
          <Dialog
            open={completeReviewDialogOpen}
            onClose={handleCloseCompleteReviewDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: "primary.main", color: "white" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningIcon />
                Confirm Complete Review
              </Box>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to mark the Application Review as
                completed?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                onClick={handleCloseCompleteReviewDialog}
                variant="outlined"
                color="inherit"
                disabled={updatingApplicationReview}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCompleteApplicationReview}
                variant="contained"
                color="primary"
                disabled={updatingApplicationReview}
                startIcon={
                  updatingApplicationReview ? (
                    <CircularProgress size={20} />
                  ) : null
                }
              >
                {updatingApplicationReview ? "Completing..." : "Confirm"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Send to NABL Confirmation Dialog */}
          <Dialog
            open={sendToNABLDialogOpen}
            onClose={() => setSendToNABLDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: "primary.main", color: "white" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UploadIcon />
                Send Assessment to NABL
              </Box>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to send the assessment to NABL?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action will submit the assessment for NABL review.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                onClick={() => setSendToNABLDialogOpen(false)}
                variant="outlined"
                color="inherit"
                disabled={sendingToNABL}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendToNABL}
                variant="contained"
                color="primary"
                disabled={sendingToNABL}
                startIcon={
                  sendingToNABL ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UploadIcon />
                  )
                }
              >
                {sendingToNABL ? "Sending to NABL..." : "Yes, Send to NABL"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Lightbox Modal */}
          <Dialog
            open={lightboxOpen}
            onClose={handleCloseLightbox}
            maxWidth={false}
            PaperProps={{
              sx: {
                backgroundColor: "transparent",
                boxShadow: "none",
                maxWidth: "90vw",
                maxHeight: "90vh",
              },
            }}
            BackdropProps={{
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              <IconButton
                onClick={handleCloseLightbox}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 1,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              {lightboxImage && (
                <img
                  src={lightboxImage}
                  alt="Enlarged view"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "85vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onClick={handleCloseLightbox}
                />
              )}
            </Box>
          </Dialog>

          {/* Trail Dialog */}
          <Dialog
            open={trailDialogOpen}
            onClose={handleCloseTrailDialog}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
              },
            }}
          >
            <DialogTitle
              sx={{
                borderBottom: `1px solid ${colors.grey[200]}`,
                backgroundColor: colors.grey[50],
                fontWeight: 600,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TimelineIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  NC Trail History
                </Typography>
                {selectedTrailData && (
                  <Chip
                    label={`NC${String(nonConformities.indexOf(selectedTrailData) + 1).padStart(3, "0")}`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {selectedTrailData && (
                <Box>
                  {/* NC Basic Info */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: colors.grey[50],
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Non-Conformity Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Clause:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedTrailData.clauseno}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Description:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedTrailData.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Trail Timeline */}
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Response Trail
                  </Typography>

                  {selectedTrailData.ncreply &&
                  selectedTrailData.ncreply.length > 0 ? (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="medium">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: colors.grey[100] }}>
                            <TableCell>
                              <b>S.No</b>
                            </TableCell>
                            <TableCell>
                              <b>Reply By</b>
                            </TableCell>
                            <TableCell>
                              <b>Date & Time</b>
                            </TableCell>
                            <TableCell>
                              <b>Assessor Remarks</b>
                            </TableCell>
                            <TableCell>
                              <b>Root Cause & Document By CAB</b>
                            </TableCell>
                            <TableCell>
                              <b>Corrective Action & Document By CAB</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTrailData.ncreply.map((reply, index) => (
                            <TableRow key={reply.id} hover>
                              <TableCell>
                                <Avatar
                                  sx={{
                                    bgcolor: colors.primary.main,
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {index + 1}
                                </Avatar>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={reply.replyby}
                                  color="primary"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {new Date(reply.insertdate).toLocaleString(
                                    "en-GB",
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    },
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                {reply.replyRemarks ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      p: 1,
                                      backgroundColor: alpha(
                                        colors.warning.main,
                                        0.1,
                                      ),
                                      borderRadius: 1,
                                      borderLeft: `3px solid ${colors.warning.main}`,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {reply.replyRemarks}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ fontStyle: "italic" }}
                                  >
                                    No remarks provided
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {/* Root Cause Text */}
                                  {reply.rootcause ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        p: 1,
                                        backgroundColor: alpha(
                                          colors.warning.main,
                                          0.1,
                                        ),
                                        borderRadius: 1,
                                        borderLeft: `3px solid ${colors.warning.main}`,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {reply.rootcause}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      No root cause provided
                                    </Typography>
                                  )}

                                  {/* Root Cause Document */}
                                  {reply.rootcauseupload && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          color: colors.grey[700],
                                          mb: 1,
                                          display: "block",
                                        }}
                                      >
                                        Supporting Document:
                                      </Typography>
                                      <WorkingSpaceDocument
                                        doc={reply.rootcauseupload}
                                        index={0}
                                        isPdf={reply.rootcauseupload
                                          .toLowerCase()
                                          .endsWith(".pdf")}
                                        isExcel={reply.rootcauseupload
                                          .toLowerCase()
                                          .match(/\.(xls|xlsx)$/)}
                                        colors={colors}
                                        onImageClick={handleOpenLightbox}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell sx={{ maxWidth: 300 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {/* Corrective Action Text */}
                                  {reply.correctiveAction ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        p: 1,
                                        backgroundColor: alpha(
                                          colors.success.main,
                                          0.1,
                                        ),
                                        borderRadius: 1,
                                        borderLeft: `3px solid ${colors.success.main}`,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {reply.correctiveAction}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      No corrective action provided
                                    </Typography>
                                  )}

                                  {/* Corrective Action Document */}
                                  {reply.correctiveActionupload && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          color: colors.grey[700],
                                          mb: 1,
                                          display: "block",
                                        }}
                                      >
                                        Supporting Document:
                                      </Typography>
                                      <WorkingSpaceDocument
                                        doc={reply.correctiveActionupload}
                                        index={0}
                                        isPdf={reply.correctiveActionupload
                                          .toLowerCase()
                                          .endsWith(".pdf")}
                                        isExcel={reply.correctiveActionupload
                                          .toLowerCase()
                                          .match(/\.(xls|xlsx)$/)}
                                        colors={colors}
                                        onImageClick={handleOpenLightbox}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: colors.grey[500],
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 48, mb: 2 }} />
                      <Typography variant="body1">
                        No trail data available for this NC
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                p: 3,
                borderTop: `1px solid ${colors.grey[200]}`,
                backgroundColor: colors.grey[50],
              }}
            >
              <Button
                onClick={handleCloseTrailDialog}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Clarification Dialog */}
          <Dialog
            open={clarificationDialogOpen}
            onClose={handleCloseClarification}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
              },
            }}
          >
            <DialogTitle
              sx={{
                borderBottom: `1px solid ${colors.grey[200]}`,
                backgroundColor: colors.grey[50],
                fontWeight: 600,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ReplyIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Reply to CAB for Clarification
                </Typography>
                {selectedNcForClarification && (
                  <Chip
                    label={`NC${String(nonConformities.indexOf(selectedNcForClarification) + 1).padStart(3, "0")}`}
                    color="secondary"
                    size="small"
                  />
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {selectedNcForClarification && (
                <Box>
                  {/* NC Basic Info */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: colors.grey[50],
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Non-Conformity Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Clause:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedNcForClarification.clause}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Description:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedNcForClarification.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Clarification Remarks */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      Clarification Required
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      Please provide your remarks for clarification that will be
                      sent to the CAB:
                    </Typography>
                    <RadioGroup
                      row
                      value={clarificationType}
                      onChange={(e) => setClarificationType(e.target.value)}
                    >
                      <FormControlLabel
                        value="0"
                        control={<Radio color="secondary" />}
                        label="Keep This Open (NC is not close for Lab. Lab can reply for this NC also.)"
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio color="secondary" />}
                        label="Close This NC (NC will close.)"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio color="secondary" />}
                        label="Unable to Close (NC will close for Lab but remain visible to NABL. Lab cannot reply further.)"
                      />
                    </RadioGroup>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={clarificationRemarks}
                      onChange={(e) => setClarificationRemarks(e.target.value)}
                      placeholder="Enter your clarification request here..."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover": {
                            "& > fieldset": {
                              borderColor: colors.secondary.main,
                            },
                          },
                          "&.Mui-focused": {
                            "& > fieldset": {
                              borderColor: colors.secondary.main,
                            },
                          },
                        },
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      This message will be sent as an assessor clarification
                      request to the CAB.
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                p: 3,
                borderTop: `1px solid ${colors.grey[200]}`,
                backgroundColor: colors.grey[50],
                gap: 2,
              }}
            >
              <Button
                onClick={handleCloseClarification}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendClarification}
                variant="contained"
                color="secondary"
                disabled={
                  !clarificationRemarks.trim() ||
                  sendingClarification ||
                  clarificationType === ""
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 120,
                }}
              >
                {sendingClarification ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    Sending...
                  </Box>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Send to Lab Confirmation Dialog */}
          <Dialog
            open={sendToLabConfirmDialogOpen}
            onClose={() => setSendToLabConfirmDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "warning.main",
                color: "white",
                textAlign: "center",
              }}
            >
              <ArrowForwardIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Send to Lab Confirmation
            </DialogTitle>
            <DialogContent sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Are you sure you want to send this assessment back to the Lab?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action will notify the lab about the assessment status and
                they will be able to take further action.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: "center", gap: 2 }}>
              <Button
                onClick={() => setSendToLabConfirmDialogOpen(false)}
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmedSendToLab}
                variant="contained"
                color="warning"
                disabled={sendingToLabFromNC}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {sendingToLabFromNC ? "Sending..." : "Yes, Send to Lab"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Send to NABL Confirmation Dialog */}
          <Dialog
            open={sendToNABLConfirmDialogOpen}
            onClose={() => setSendToNABLConfirmDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "success.main",
                color: "white",
                textAlign: "center",
              }}
            >
              <CheckCircleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Send to NABL Confirmation
            </DialogTitle>
            <DialogContent sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Are you sure you want to send this assessment to NABL?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action will finalize the assessment and send it to NABL for
                final review and approval.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: "center", gap: 2 }}>
              <Button
                onClick={() => setSendToNABLConfirmDialogOpen(false)}
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmedSendToNABL}
                variant="contained"
                color="success"
                disabled={sendingToNABLFromNC}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {sendingToNABLFromNC ? "Sending..." : "Yes, Send to NABL"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

// Meeting Image Component to handle async S3 URL generation
const MeetingImage = ({ doc, index, colors, onImageClick }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        if (doc.imgfile) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc.imgfile)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(
              doc.imgfile,
            );
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc.imgfile);
          }
        }
      } catch (error) {
        console.error("Error loading image:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [doc.imgfile]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 120,
          height: 120,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 120,
        height: 120,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Document ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

// Attendance Image Component to handle async S3 URL generation
const AttendanceImage = ({ doc, index, colors, onImageClick }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        if (doc.imgfile) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc.imgfile)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(
              doc.imgfile,
            );
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc.imgfile);
          }
        }
      } catch (error) {
        console.error("Error loading attendance image:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [doc.imgfile]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 120,
          height: 120,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick
            ? onImageClick(imageUrl)
            : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Attendance Image ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
          }}
        />
      </Box>
      {doc.date && (
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 500,
            maxWidth: 120,
            wordWrap: "break-word",
          }}
        >
          {new Date(doc.date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })}
        </Typography>
      )}
    </Box>
  );
};

// Working Space Document Component to handle images and PDF/Excel thumbnails
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  // Check if doc contains multiple files separated by ~
  if (doc && doc.includes("~")) {
    const files = doc.split("~");
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {files.map((file, fileIndex) => {
          const cleanFile = file.trim();
          if (!cleanFile) return null;

          const fileIsPdf = cleanFile.toLowerCase().endsWith(".pdf");
          const fileIsExcel = cleanFile.toLowerCase().match(/\.(xls|xlsx)$/);

          return (
            <Box
              key={fileIndex}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  minWidth: 50,
                }}
              >
                Doc {fileIndex + 1}:
              </Typography>
              <SingleDocument
                doc={cleanFile}
                index={fileIndex}
                isPdf={fileIsPdf}
                isExcel={fileIsExcel}
                colors={colors}
                onImageClick={onImageClick}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  // Single file - use the original logic
  return (
    <SingleDocument
      doc={doc}
      index={index}
      isPdf={isPdf}
      isExcel={isExcel}
      colors={colors}
      onImageClick={onImageClick}
    />
  );
};

// Single Document Component - extracted from the original WorkingSpaceDocument
const SingleDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For PDF/Excel files, show thumbnail with icon
  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  // For images, show the actual image
  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

export default AssessmentDashboard;
